import React from "react";
import { Link } from "react-router-dom";
import WizardStepper from "../../components/WizardStepper";
import AiDisclaimer from "../../components/AiDisclaimer";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import RadioCard from "../../ui/RadioCard";
import Badge from "../../ui/Badge";
import Spinner from "../../ui/Spinner";
import Progress from "../../ui/Progress";
import {
  attachGuidelineToJob,
  createJob,
  deleteJobPhotos,
  deleteGuideline,
  downloadAnnotatedPhotoZip,
  downloadEstimatorPack,
  getBuildStatus,
  listGuidelines,
  listJobPhotos,
  lookupPublicSafetyContacts,
  patchJobPhoto,
  reorderJobPhotos,
  reanalyzeJobPhoto,
  relinkJobPhotos,
  fetchJobPhotoBlobUrl,
  pollEstimatorPackReady,
  runBuild,
  uploadGuidelines,
  uploadPhotos,
  uploadSketch,
  updateJob,
} from "../../api/build";
import { getBillingSummary } from "../../api/billing";
import { extractCrmIntake } from "../../api/crm";
import { listJobs as listExistingJobs } from "../../api/jobs";
import { standardizeCapitalization } from "../../utils/textFormat";

const STEP_IDS = {
  A0: "A0_PHOTOS_UPLOAD",
  A1: "A1_PHOTO_LABELS",
  A2: "A2_GUIDELINES_UPLOAD",
  A3: "A3_PLATFORM_SELECT",
  A4: "A4_SKETCH_UPLOAD",
  A5: "A5_STATUS_PROCESSING",
  A6: "A6_LABEL_REVIEW",
  A7: "A7_LINE_ITEM_REVIEW",
};

const WIZARD_STEPS = [
  { id: STEP_IDS.A0, label: "Photos Upload" },
  { id: STEP_IDS.A1, label: "Photo Label Choice" },
  { id: STEP_IDS.A2, label: "Estimating Guidelines Upload" },
  { id: STEP_IDS.A3, label: "Platform Select" },
  { id: STEP_IDS.A4, label: "Sketch Upload" },
  { id: STEP_IDS.A5, label: "Status / Processing" },
  { id: STEP_IDS.A6, label: "Label Review" },
  { id: STEP_IDS.A7, label: "Line Item Review" },
];

const PROCESS_PHASES = ["Creating job", "Uploading", "Running", "Packaging", "Ready"];
const STORAGE_KEY = "build_wizard_state_v4";
const LOSS_TYPE_OPTIONS = ["fire", "water", "wind", "vehicle", "vandalism", "theft", "other"];
const CIVIC_CONTACT_LOSS_TYPES = new Set(["fire", "vehicle", "vandalism", "theft"]);

function toTitleCase(value) {
  return String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1).toLowerCase())
    .join(" ");
}

function createInitialState() {
  return {
    currentStepId: STEP_IDS.A0,
    jobId: "",
    setId: "",
    packRef: null,
    photos: [],
    jobPhotos: [],
    photoIds: [],
    photoSyncError: "",
    wantsPhotoLabels: null,
    wantsLineItemSuggestions: null,
    lossType: "",
    wantsCodeUpgrades: null,
    propertyZip: "",
    guideline: null,
    guidelineDocumentId: "",
    guidelineSkipped: false,
    platform: "",
    sketch: null,
    sketchDocumentId: "",
    sketchSkipped: false,
    processingPhase: "Creating job",
    processingMessage: "",
    processingProgress: 0,
    processingDone: false,
    processingError: "",
    buildRunRequested: false,
    packReady: false,
    downloadingPack: false,
    downloadingAnnotatedPhotos: false,
    availableGuidelines: [],
    selectedGuidelineId: "",
    lineItems: [],
    copiedCodeIndex: -1,
    uploadProgress: 0,
    uploadingPhotos: false,
    uploadStatusText: "",
    guidelineProgress: 0,
    sketchProgress: 0,
    scopeProgress: 0,
    packageProgress: 0,
    lineItemsLoading: false,
    lineItemsConfirmedEmpty: false,
    jobInfo: {
      jobName: "",
      insuredName: "",
      claimNumber: "",
      propertyAddress: "",
      notes: "",
      savedAt: "",
    },
  };
}

function safeLoadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialState();
    const parsed = JSON.parse(raw);
    const initial = createInitialState();
    const minimalPhotos = Array.isArray(parsed?.photos)
      ? parsed.photos
          .map((p) => ({
            id: p?.id ? String(p.id) : "",
            filename: p?.filename ? String(p.filename) : p?.name ? String(p.name) : "",
            storage_url: p?.storage_url ? String(p.storage_url) : "",
            room: p?.room ?? null,
            damage_type: p?.damage_type ?? null,
            description: p?.description ?? null,
            thumbnail_url: "",
          }))
          .filter((p) => p.id || p.filename || p.storage_url)
      : [];

    return {
      ...initial,
      currentStepId: parsed?.currentStepId || initial.currentStepId,
      jobId: parsed?.jobId || "",
      setId: parsed?.setId || "",
      platform: parsed?.platform || parsed?.selectedPlatform || "",
      guidelineDocumentId: parsed?.guidelineDocumentId || "",
      sketchDocumentId: parsed?.sketchDocumentId || "",
      photoIds: Array.isArray(parsed?.photoIds) ? parsed.photoIds.map((id) => String(id)) : [],
      wantsPhotoLabels:
        typeof parsed?.wantsPhotoLabels === "boolean" ? parsed.wantsPhotoLabels : initial.wantsPhotoLabels,
      wantsLineItemSuggestions:
        typeof parsed?.wantsLineItemSuggestions === "boolean"
          ? parsed.wantsLineItemSuggestions
          : initial.wantsLineItemSuggestions,
      lossType: LOSS_TYPE_OPTIONS.includes(String(parsed?.lossType || "").toLowerCase())
        ? String(parsed?.lossType || "").toLowerCase()
        : "",
      wantsCodeUpgrades:
        typeof parsed?.wantsCodeUpgrades === "boolean" ? parsed.wantsCodeUpgrades : initial.wantsCodeUpgrades,
      propertyZip: typeof parsed?.propertyZip === "string" ? parsed.propertyZip : "",
      photos: minimalPhotos,
      jobPhotos: minimalPhotos,
      jobInfo: {
        ...initial.jobInfo,
        ...(parsed?.jobInfo && typeof parsed.jobInfo === "object" ? parsed.jobInfo : {}),
      },
    };
  } catch (e) {
    return createInitialState();
  }
}

function sanitizeBuildStateForStorage(state, { compact = false } = {}) {
  const sourcePhotos = Array.isArray(state?.jobPhotos) && state.jobPhotos.length > 0
    ? state.jobPhotos
    : Array.isArray(state?.photos)
      ? state.photos
      : [];

  const minimalPhotos = compact
    ? []
    : sourcePhotos
        .map((p) => ({
          id: p?.id ? String(p.id) : "",
          filename: p?.filename ? String(p.filename) : p?.name ? String(p.name) : "",
          storage_url: p?.storage_url ? String(p.storage_url) : "",
          room: p?.room ?? null,
          damage_type: p?.damage_type ?? null,
          description: p?.description ?? null,
        }))
        .filter((p) => p.id || p.filename || p.storage_url);

  return {
    currentStepId: state?.currentStepId || STEP_IDS.A0,
    jobId: state?.jobId || "",
    setId: state?.setId || "",
    platform: state?.platform || "",
    selectedPlatform: state?.platform || "",
    guidelineDocumentId: state?.guidelineDocumentId || "",
    sketchDocumentId: state?.sketchDocumentId || "",
    photoIds: Array.isArray(state?.photoIds) ? state.photoIds.map((id) => String(id)) : [],
    wantsPhotoLabels: typeof state?.wantsPhotoLabels === "boolean" ? state.wantsPhotoLabels : null,
    wantsLineItemSuggestions:
      typeof state?.wantsLineItemSuggestions === "boolean" ? state.wantsLineItemSuggestions : null,
    lossType: LOSS_TYPE_OPTIONS.includes(String(state?.lossType || "").toLowerCase())
      ? String(state?.lossType || "").toLowerCase()
      : "",
    wantsCodeUpgrades: typeof state?.wantsCodeUpgrades === "boolean" ? state.wantsCodeUpgrades : null,
    propertyZip: String(state?.propertyZip || ""),
    photos: minimalPhotos,
    jobInfo: {
      jobName: String(state?.jobInfo?.jobName || ""),
      insuredName: String(state?.jobInfo?.insuredName || ""),
      claimNumber: String(state?.jobInfo?.claimNumber || ""),
      propertyAddress: String(state?.jobInfo?.propertyAddress || ""),
      notes: String(state?.jobInfo?.notes || ""),
      savedAt: String(state?.jobInfo?.savedAt || ""),
    },
  };
}

function isStorageQuotaError(error) {
  const name = String(error?.name || "");
  const code = Number(error?.code || 0);
  const msg = String(error?.message || "").toLowerCase();
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22 ||
    code === 1014 ||
    msg.includes("quota")
  );
}

function isFreePlanScopeGateError(message) {
  const text = String(message || "").toLowerCase();
  return text.includes("scope suggestions") && text.includes("free plan");
}

function toFileMeta(file) {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    thumbnail_url: "",
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}

function phaseIndex(phase) {
  const idx = PROCESS_PHASES.indexOf(phase);
  return idx >= 0 ? idx : 0;
}

function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function resolveThumbUrl(storageUrl) {
  const s = String(storageUrl || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
  return "";
}

function cleanOptional(value) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;
  return text;
}

function isUnknownDamageTypeValue(value) {
  const text = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!text) return true;
  return (
    text === "needs review" ||
    text === "damage: needs review" ||
    text === "unknown" ||
    text === "unspecified" ||
    text === "n/a" ||
    text === "na" ||
    text === "none" ||
    text === "null"
  );
}

function damageTypeFromLossType(lossType) {
  const normalized = String(lossType || "").trim().toLowerCase();
  if (!LOSS_TYPE_OPTIONS.includes(normalized)) return "";
  return toTitleCase(normalized);
}

function isPhotoIncomplete(photo) {
  const room = String(photo?.room || "").trim();
  const description = String(photo?.description || "").trim();
  const confidence = Number(photo?.confidence);
  return !room || !description || !(Number.isFinite(confidence) && confidence > 0);
}

function firstNonEmptyString(...values) {
  for (const value of values) {
    if (value === null || typeof value === "undefined") continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function getPhotoLabelFields(photo) {
  const analysis =
    photo?.analysis && typeof photo.analysis === "object"
      ? photo.analysis
      : photo?.vision && typeof photo.vision === "object"
        ? photo.vision
        : {};

  const confidenceRaw = firstNonEmptyString(
    photo?.confidence,
    photo?.vision_confidence,
    photo?.vision_confidence_damage,
    photo?.vision_confidence_room,
    analysis?.confidence,
    analysis?.vision_confidence,
    analysis?.vision_confidence_damage,
    analysis?.vision_confidence_room
  );
  const parsedConfidence = Number(confidenceRaw);

  const room = firstNonEmptyString(
      photo?.room,
      photo?.room_label,
      photo?.roomLabel,
      analysis?.room,
      analysis?.room_label,
      analysis?.roomLabel
    );
  const description = firstNonEmptyString(
      photo?.description,
      photo?.damage_description,
      photo?.damageDescription,
      analysis?.description,
      analysis?.damage_description,
      analysis?.damageDescription
    );

  return {
    room,
    damageType: firstNonEmptyString(
      photo?.damage_type,
      photo?.damageType,
      photo?.damage_label,
      photo?.damageLabel,
      analysis?.damage_type,
      analysis?.damageType,
      analysis?.damage_label,
      analysis?.damageLabel
    ),
    description: stripTextureFromDescription(stripRoomFromDescription(room, description)),
    confidence: Number.isFinite(parsedConfidence) && parsedConfidence > 0 ? parsedConfidence : null,
  };
}

function parseQuantityHintsFromText(text, context = "", analysis = null) {
  const raw = `${String(text || "")} ${String(context || "")}`.toLowerCase();
  const measurements = [];
  let sf = null;
  let lf = null;
  let ea = null;
  let hr = null;

  const pushMax = (curr, next) => {
    if (!Number.isFinite(Number(next))) return curr;
    const n = Number(next);
    if (!Number.isFinite(Number(curr))) return n;
    return Math.max(Number(curr), n);
  };

  const reFindAll = (re) => {
    const out = [];
    let m;
    while ((m = re.exec(raw)) !== null) {
      out.push(m);
    }
    return out;
  };

  reFindAll(/(\d+(?:\.\d+)?)\s*(sf|sq\.?\s*ft|sqft|square\s*feet)\b/g).forEach((m) => {
    const val = Number(m[1]);
    sf = pushMax(sf, val);
    measurements.push({ kind: "area", value: val, unit: "sf", target: "affected area", source: "inferred_text" });
  });

  reFindAll(/(\d+(?:\.\d+)?)\s*(lf|linear\s*feet|lineal\s*feet)\b/g).forEach((m) => {
    const val = Number(m[1]);
    lf = pushMax(lf, val);
    measurements.push({ kind: "length", value: val, unit: "lf", target: "linear extent", source: "inferred_text" });
  });

  reFindAll(/(\d+(?:\.\d+)?)\s*(ea|each)\b/g).forEach((m) => {
    const val = Number(m[1]);
    ea = pushMax(ea, val);
    measurements.push({ kind: "count", value: val, unit: "ea", target: "count", source: "inferred_text" });
  });

  reFindAll(/(\d+(?:\.\d+)?)\s*(hr|hrs|hour|hours)\b/g).forEach((m) => {
    const val = Number(m[1]);
    hr = pushMax(hr, val);
    measurements.push({ kind: "time", value: val, unit: "hr", target: "labor time", source: "inferred_text" });
  });

  // Dimension pair like "4x8" can provide area hint for drywall-like contexts.
  reFindAll(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\b/g).forEach((m) => {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return;
    if (!/\bdrywall|wall|ceiling|sheetrock|patch|flood cut|flood_cut\b/.test(raw)) return;
    const area = a * b;
    if (area > 0) {
      sf = pushMax(sf, area);
      measurements.push({ kind: "area", value: area, unit: "sf", target: "opening area", source: "inferred_text" });
    }
  });

  if (analysis && Array.isArray(analysis.measurements)) {
    analysis.measurements.forEach((m) => {
      if (!m || typeof m !== "object") return;
      const value = Number(m.value);
      if (!Number.isFinite(value)) return;
      const unit = String(m.unit || "").toLowerCase();
      const kind = String(m.kind || "").toLowerCase();
      if (unit === "sf" || kind === "area") sf = pushMax(sf, value);
      if (unit === "lf" || kind === "length") lf = pushMax(lf, value);
      if (unit === "ea" || kind === "count") ea = pushMax(ea, value);
      if (unit === "hr" || kind === "time") hr = pushMax(hr, value);
      measurements.push({
        kind: kind || "unknown",
        value,
        unit: unit || "unknown",
        target: String(m.target || "measurement"),
        source: String(m.source || "vision"),
      });
    });
  }

  return { sf, lf, ea, hr, measurements };
}

function extractStructuredSignals(photo, labels) {
  const filename = String(photo?.filename || "").toLowerCase();
  const damageType = String(labels?.damageType || "").toLowerCase();
  const desc = String(labels?.description || "").toLowerCase();
  const text = `${filename} ${damageType} ${desc}`;
  const tags = new Set();
  const materialsMissing = [];
  const materialsPresent = [];
  const analysis = photo?.analysis && typeof photo.analysis === "object" ? photo.analysis : {};
  const markers = analysis?.markers && typeof analysis.markers === "object" ? analysis.markers : {};
  const markerText = [
    markers.primary_surface,
    ...(Array.isArray(markers.surfaces_visible) ? markers.surfaces_visible : []),
    ...(Array.isArray(markers.damage_observed) ? markers.damage_observed : []),
    ...(Array.isArray(markers.removed_or_missing) ? markers.removed_or_missing : []),
    ...(Array.isArray(markers.structural_exposure) ? markers.structural_exposure : []),
    ...(Array.isArray(markers.damage_locations) ? markers.damage_locations : []),
  ].filter(Boolean).join(" ").toLowerCase();
  const surfaceText = `${text} ${markerText}`;

  if (/\bwater|leak|stain|wet|moisture|flood\b/.test(text)) {
    tags.add("water");
  }
  if (/\bflood\s*cut|flood_cut|cut\s*out|cutout|demo|opened\s+cavity|open\s+cavity\b/.test(text)) {
    tags.add("flood_cut");
    tags.add("cutout");
  }
  if (/\bdrywall|sheetrock|gyp\b/.test(surfaceText)) {
    tags.add("drywall");
    materialsPresent.push("drywall");
  }
  if (/\bwall\b/.test(surfaceText)) tags.add("wall");
  if (/\bceiling\b/.test(surfaceText)) tags.add("ceiling");
  const wallOrCeiling = /\bwall\b|\bceiling\b/.test(surfaceText);
  const openWallSignal = /\bhole\b|\bopening\b|\bmissing (?:material|section|surface)\b|\bopen(?:ed)? (?:cavity|wall)\b|\bexposed (?:studs?|framing|underlying material)\b/.test(surfaceText);
  if (wallOrCeiling && openWallSignal) {
    tags.add("drywall");
    tags.add("open_cavity");
    tags.add("missing_drywall");
    materialsMissing.push("drywall");
  }
  if (/\binsulation|batt|fiberglass|cellulose|r13|r19|r30\b/.test(text)) {
    tags.add("insulation");
    materialsPresent.push("insulation");
  }
  if (/\bmissing\s+drywall|drywall\s+removed|removed\s+drywall\b/.test(text)) {
    materialsMissing.push("drywall");
    tags.add("missing_drywall");
  }
  if (/\bmissing\s+insulation|insulation\s+removed|removed\s+insulation|wet\s+insulation|saturated\s+insulation\b/.test(text)) {
    materialsMissing.push("insulation");
    tags.add("missing_insulation");
    tags.add("saturated");
  }
  if (/\btoilet\b/.test(text)) tags.add("toilet");
  if (/\bdetach|detached|reset|pull\s+and\s+reset|remove\s+and\s+reset\b/.test(text)) {
    tags.add("detach");
    tags.add("reset");
    tags.add("detach_reset");
  }
  if (/\btub|bathtub|shower\b/.test(text)) tags.add("bathtub");
  if (/\bcontent|contents|furniture|belongings|personal property\b/.test(text)) tags.add("content");
  if (/\bmove|moved|moving|protect|covered|packout|relocate|staged\b/.test(text)) {
    tags.add("move");
    tags.add("manipulation");
    tags.add("protect");
  }
  if (/\borange peel|knockdown|popcorn|smooth|skip trowel|texture\b/.test(text)) tags.add("texture");

  const removedItems = Array.isArray(analysis.removed_items) ? analysis.removed_items : [];
  removedItems.forEach((x) => {
    const s = String(x || "").toLowerCase();
    if (!s) return;
    tags.add(`removed_${s.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")}`);
    if (/drywall|sheetrock/.test(s)) {
      materialsMissing.push("drywall");
      tags.add("missing_drywall");
    }
    if (/insulation|batt|fiberglass|cellulose/.test(s)) {
      materialsMissing.push("insulation");
      tags.add("missing_insulation");
    }
    if (/toilet/.test(s)) tags.add("toilet");
    if (/vanity/.test(s)) tags.add("vanity");
    if (/bathtub|tub/.test(s)) tags.add("bathtub");
    if (/shower/.test(s)) tags.add("shower");
    if (/medicine cabinet|medicine_cabinet/.test(s)) tags.add("medicine_cabinet");
    if (/dishwasher/.test(s)) tags.add("dishwasher");
    if (/refrigerator|fridge/.test(s)) tags.add("refrigerator");
  });

  const suggestedRepairs = Array.isArray(analysis.suggested_repairs) ? analysis.suggested_repairs : [];
  suggestedRepairs.forEach((x) => {
    const s = String(x || "").toLowerCase();
    if (!s) return;
    if (/detach|reset/.test(s)) {
      tags.add("detach");
      tags.add("reset");
      tags.add("detach_reset");
    }
    if (/replace|remove\/replace|r&r/.test(s)) tags.add("replace");
    if (/toilet/.test(s)) tags.add("toilet");
    if (/vanity/.test(s)) tags.add("vanity");
    if (/bathtub|tub/.test(s)) tags.add("bathtub");
    if (/shower/.test(s)) tags.add("shower");
    if (/medicine cabinet|medicine_cabinet/.test(s)) tags.add("medicine_cabinet");
    if (/dishwasher/.test(s)) tags.add("dishwasher");
    if (/refrigerator|fridge/.test(s)) tags.add("refrigerator");
  });

  let finishTexture = "";
  if (/\bknockdown\b/.test(text)) finishTexture = "knockdown";
  else if (/\borange peel\b/.test(text)) finishTexture = "orange_peel";
  else if (/\bpopcorn\b/.test(text)) finishTexture = "popcorn";
  else if (/\bsmooth\b/.test(text)) finishTexture = "smooth";

  return {
    tags: Array.from(tags),
    materialsMissing: Array.from(new Set(materialsMissing)),
    materialsPresent: Array.from(new Set(materialsPresent)),
    finishTexture,
    lossType: tags.has("water") ? "water" : "",
    removedItems: removedItems.map((x) => String(x || "").trim()).filter(Boolean),
    suggestedRepairs: suggestedRepairs.map((x) => String(x || "").trim()).filter(Boolean),
  };
}

function buildObservationsFromJobPhotos(jobPhotos, { includeMeasurements = true } = {}) {
  const rows = Array.isArray(jobPhotos) ? jobPhotos.filter((p) => p && p.id) : [];
  return rows.map((photo) => {
    const labels = getPhotoLabelFields(photo);
    const parsedConfidence = Number(labels.confidence);
    const qty = parseQuantityHintsFromText(labels.description, labels.damageType, photo?.analysis || null);
    const sig = extractStructuredSignals(photo, labels);
    const meta = {};
    if (sig.tags.length > 0) meta.tags = sig.tags;
    if (sig.lossType) meta.loss_type = sig.lossType;
    if (sig.finishTexture) meta.finish_texture = sig.finishTexture;
    if (sig.materialsMissing.length > 0) meta.materials_missing = sig.materialsMissing;
    if (sig.materialsPresent.length > 0) meta.materials_present = sig.materialsPresent;
    if (sig.materialsMissing.includes("drywall")) meta.drywall_missing = true;
    if (sig.materialsMissing.includes("insulation")) meta.insulation_missing = true;
    if (sig.removedItems.length > 0) meta.removed_items = sig.removedItems;
    if (sig.suggestedRepairs.length > 0) meta.suggested_repairs = sig.suggestedRepairs;
    if (includeMeasurements) {
      if (Number.isFinite(Number(qty.sf))) meta.sf = Number(qty.sf);
      if (Number.isFinite(Number(qty.lf))) meta.lf = Number(qty.lf);
      if (Number.isFinite(Number(qty.ea))) meta.ea = Math.max(1, Math.round(Number(qty.ea)));
      if (Number.isFinite(Number(qty.hr))) meta.hr = Number(qty.hr);
      if (qty.measurements.length > 0) meta.measurements = qty.measurements;
    } else {
      meta.measurements_require_sketch = true;
    }

    return {
      photo_id: String(photo.id),
      room: cleanOptional(photo.room || labels.room),
      damage_type: cleanOptional(labels.damageType),
      description: cleanOptional(labels.description) || "Photo uploaded",
      confidence: Number.isFinite(parsedConfidence) && parsedConfidence > 0 ? parsedConfidence : 0.8,
      needs_review: false,
      meta,
    };
  });
}

function normalizeLineItems(items, { includeValues = true } = {}) {
  const TASK_QTY_FALLBACKS = {
    drywall_remove_replace: 4,
    drywall_tape_float: 4,
    texture_match: 4,
    prime_paint: 16,
    baseboard_remove_replace: 8,
    detach_reset_toilet: 1,
    toilet_remove_replace: 1,
    labor_minimum_vinyl: 1,
    labor_minimum_tile: 1,
    labor_minimum_cleaning: 1,
  };
  const pickQty = (row) => {
    const direct = row?.qty ?? row?.quantity;
    if (direct !== null && typeof direct !== "undefined" && String(direct).trim() !== "") return direct;
    const applied = row?.applied_rules_json || row?.applied_rules;
    let parsed = null;
    if (typeof applied === "string" && applied.trim()) {
      try {
        parsed = JSON.parse(applied);
      } catch (_e) {
        parsed = null;
      }
    } else if (applied && typeof applied === "object") {
      parsed = applied;
    }
    const meta = parsed?.meta || {};
    if (meta.qty_rounded !== null && typeof meta.qty_rounded !== "undefined") return meta.qty_rounded;
    if (meta.qty_raw !== null && typeof meta.qty_raw !== "undefined") return meta.qty_raw;
    const taskId = String(row?.canonical_key || row?.canonical_task_id || "").trim();
    if (taskId && Object.prototype.hasOwnProperty.call(TASK_QTY_FALLBACKS, taskId)) {
      return TASK_QTY_FALLBACKS[taskId];
    }
    const unit = String(row?.unit || "").trim().toUpperCase();
    if (unit === "EA") return 1;
    if (unit === "HR") return 1;
    if (unit === "LF") return 1;
    if (unit === "SF") return 1;
    return "";
  };
  const rows = Array.isArray(items) ? items : [];
  return rows.map((row, index) => {
    const applied = row?.applied_rules_json || row?.applied_rules;
    let parsed = null;
    if (typeof applied === "string" && applied.trim()) {
      try {
        parsed = JSON.parse(applied);
      } catch (_e) {
        parsed = null;
      }
    } else if (applied && typeof applied === "object") {
      parsed = applied;
    }
    const meta = parsed?.meta && typeof parsed.meta === "object" ? parsed.meta : {};
    const tags = Array.isArray(meta.tags_clean) ? meta.tags_clean.map((x) => String(x || "")) : [];
    const surfaceArea = String(meta.surface_area || meta.surfaceArea || "").trim();
    return {
      room: String(row?.room || "").trim() || "(unspecified)",
      description: String(row?.description || row?.label || "").trim(),
      qty: includeValues ? pickQty(row) : "",
      unit: String(row?.unit || "").trim(),
      confidence: row?.confidence ?? "",
      canonicalKey: String(row?.canonical_key || row?.canonical_task_id || "").trim(),
      areaHint: ["Ceiling", "Walls", "Floors", "Other"].includes(surfaceArea)
        ? surfaceArea
        : inferLineItemAreaFromSignals({
            description: row?.description || row?.label || "",
            canonicalKey: row?.canonical_key || row?.canonical_task_id || "",
            tags,
          }),
      key: `${index}:${String(row?.id || row?.canonical_key || row?.canonical_task_id || "row")}`,
    };
  });
}

function buildFallbackLineItemsFromPhotos(jobPhotos, _platform = "xactimate", { includeValues = true } = {}) {
  const rows = Array.isArray(jobPhotos) ? jobPhotos : [];
  const out = [];
  const seen = new Set();
  const add = (description, qty = 1, unit = "EA", room = "(unspecified)") => {
    const key = `${room}|${description}|${unit}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      room,
      description,
      qty: includeValues ? qty : "",
      unit,
      confidence: "",
      areaHint: inferLineItemAreaFromSignals({ description }),
      key,
    });
  };

  rows.forEach((p, idx) => {
    const room = String(p?.room || "").trim() || "(unspecified)";
    const txt = `${String(p?.damage_type || "")} ${String(p?.description || "")}`.toLowerCase();
    const hasWater = /water|leak|stain|wet|moisture/.test(txt);
    const hasFloor = /floor|tile|vinyl|plank|carpet|pad|mortar/.test(txt);
    const hasPaint = /paint|prime|texture|drywall|wall|ceiling|trim/.test(txt);

    if (hasWater) add("Water mitigation and dry-out assessment", 1, "EA", room);
    if (hasFloor) add("Floor covering remove/replace", 1, "EA", room);
    if (hasPaint) add("Prime and paint affected area", 1, "EA", room);
    if (!hasWater && !hasFloor && !hasPaint) {
      add(`Manual review required for photo ${idx + 1}`, 1, "EA", room);
    }
  });

  return out;
}

function inferLineItemAreaFromSignals({ description = "", canonicalKey = "", tags = [] } = {}) {
  const text = `${String(description || "")} ${String(canonicalKey || "")} ${tags.join(" ")}`.toLowerCase();
  if (/ceiling|popcorn|overhead/.test(text)) return "Ceiling";
  if (/floor|tile|vinyl|carpet|pad|plank|mortar|subfloor/.test(text)) return "Floors";
  if (/wall|drywall|paint|texture|baseboard|casing|trim|crown/.test(text)) return "Walls";
  return "Other";
}

function classifyLineItemArea(item) {
  if (["Ceiling", "Walls", "Floors", "Other"].includes(item?.areaHint)) return item.areaHint;
  return inferLineItemAreaFromSignals({
    description: `${String(item?.description || "")} ${String(item?.room || "")}`,
    canonicalKey: item?.canonicalKey || "",
  });
}

function formatRoomName(value) {
  const raw = String(value || "").trim();
  if (!raw) return "(Unspecified)";
  if (raw.startsWith("(") && raw.endsWith(")")) return "(Unspecified)";
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function stripRoomFromDescription(room, description) {
  let desc = String(description || "").trim();
  const roomText = String(room || "").trim();
  if (!desc || !roomText) return desc;
  const escaped = roomText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  desc = desc.replace(new RegExp(`^\\s*(?:room\\s*:\\s*)?${escaped}\\s*[:\\-–—,]\\s*`, "i"), "");
  desc = desc.replace(new RegExp(`^\\s*${escaped}\\s+`, "i"), "");
  desc = desc.replace(new RegExp(`\\b(?:in|inside|within)\\s+(?:the\\s+)?${escaped}\\b\\s*[,;:\\-–—]?\\s*`, "i"), "");
  desc = desc.replace(/\s{2,}/g, " ").replace(/^[\s,;:.-]+|[\s,;:.-]+$/g, "").trim();
  return desc;
}

function stripTextureFromDescription(description) {
  let desc = String(description || "").trim();
  if (!desc) return desc;
  desc = desc.replace(/\s*(?:potential\s+)?texture\s*type\s*:\s*[^.]+\.?/gi, "").trim();
  desc = desc.replace(/\b(?:orange peel|knockdown|popcorn|skip trowel|smooth)\s+texture\b/gi, "").trim();
  desc = desc.replace(/\btexture\s+(?:match|repair|finish|type|pattern|reference|sample)\b/gi, "").trim();
  desc = desc.replace(/\b(?:match|repair|identify|confirm|verify)\s+(?:wall\s+|ceiling\s+|paint\s+)?texture\b/gi, "").trim();
  desc = desc.replace(/\s*,?\s*(?:and\s+)?(?:texture\s+match|match\s+texture)\b/gi, "").trim();
  desc = desc.replace(/\s{2,}/g, " ").replace(/^[\s,;:.-]+|[\s,;:.-]+$/g, "").trim();
  return desc;
}

function roomBaseName(value) {
  const label = formatRoomName(value);
  if (!label || label === "(Unspecified)") return "";
  return label.replace(/\s+\d+$/, "").trim();
}

function inferRoomTypeFromText(photo) {
  const text = `${String(photo?.filename || "")} ${String(photo?.description || "")} ${String(photo?.damage_type || "")}`
    .toLowerCase();
  if (/\bbath(room)?\b|\btoilet\b|\bshower\b|\bvanity\b/.test(text)) return "Bathroom";
  if (/\bkitchen\b|\bcabinet\b|\bcounter(top)?\b|\bsink\b/.test(text)) return "Kitchen";
  if (/\bbed(room)?\b/.test(text)) return "Bedroom";
  if (/\bliving\b|\bfamily\b|\bden\b/.test(text)) return "Living Room";
  if (/\bhall(way)?\b|\bcorridor\b/.test(text)) return "Hallway";
  if (/\blaundry\b|\bwasher\b|\bdryer\b/.test(text)) return "Laundry";
  if (/\bgarage\b/.test(text)) return "Garage";
  return "";
}

function isGroupedDetailPhoto(photo) {
  const text = `${String(photo?.filename || "")} ${String(photo?.description || "")} ${String(photo?.damage_type || "")}`
    .toLowerCase();
  return /\b(close[- ]?up|detail|measurement|tape measure|cabinet|sink|vanity|baseboard|trim|fixture|mirror|countertop|supply line|drain)\b/.test(text);
}

function shouldKeepPreviousRoomForDetail(photo, inferredRoom, previousRoom) {
  if (!previousRoom || !isGroupedDetailPhoto(photo)) return false;
  const raw = String(photo?.room || "").trim().toLowerCase();
  const text = `${raw} ${String(photo?.filename || "")} ${String(photo?.description || "")} ${String(photo?.damage_type || "")}`
    .toLowerCase();
  const hasStrongCurrentRoom = /\b(kitchen|bathroom|bedroom|living room|family room|hallway|laundry|garage)\b/.test(text);
  if (!inferredRoom) return true;
  if (previousRoom === "Bathroom" && inferredRoom === "Kitchen" && /\b(cabinet|sink|countertop)\b/.test(text)) return true;
  return !hasStrongCurrentRoom;
}

function normalizeRoomsUnique(photos, { preferSketch = false } = {}) {
  const rows = Array.isArray(photos) ? photos : [];
  let previousRoom = "";
  return rows.map((photo, idx) => {
    const raw = String(photo?.room || "").trim();
    const currentBase = roomBaseName(raw);
    const inferred = inferRoomTypeFromText(photo);
    let base = currentBase;
    if (shouldKeepPreviousRoomForDetail(photo, inferred || base, previousRoom)) {
      base = previousRoom;
    } else if (!base) {
      if (inferred) {
        base = inferred;
      } else if (preferSketch) {
        base = "Sketch Room";
      } else {
        base = "Room";
      }
    }

    previousRoom = base || previousRoom;
    return {
      ...photo,
      room: base,
      room_normalized: base,
      room_original: raw || null,
      room_idx: idx + 1,
    };
  });
}

function groupLineItemsByArea(items) {
  const order = ["Ceiling", "Walls", "Floors", "Other"];
  const grouped = { Walls: [], Floors: [], Ceiling: [], Other: [] };
  (Array.isArray(items) ? items : []).forEach((row) => {
    grouped[classifyLineItemArea(row)].push(row);
  });
  return order
    .filter((area) => grouped[area].length > 0)
    .map((area) => ({ area, items: grouped[area] }));
}

function groupLineItemsByRoomAndArea(items) {
  const areaOrder = ["Ceiling", "Walls", "Floors", "Other"];
  const roomOrder = [];
  const roomMap = new Map();
  const rows = Array.isArray(items) ? items : [];
  rows.forEach((row) => {
    const room = formatRoomName(String(row?.room || "").trim() || "(unspecified)");
    if (!roomMap.has(room)) {
      roomMap.set(room, { Walls: [], Floors: [], Ceiling: [], Other: [] });
      roomOrder.push(room);
    }
    const area = classifyLineItemArea(row);
    roomMap.get(room)[area].push(row);
  });
  return roomOrder.map((room) => ({
    room,
    areas: areaOrder
      .filter((area) => roomMap.get(room)[area].length > 0)
      .map((area) => ({ area, items: roomMap.get(room)[area] })),
  }));
}

function mergePhotosWithPreviews(photoIds, jobPhotos, localPhotos) {
  const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
  const pickBackendText = (row, prior, key) => {
    if (hasOwn(row, key)) return row[key] ?? null;
    return prior?.[key] ?? null;
  };
  const pickText = (primary, fallback) => {
    const p = typeof primary === "string" ? primary.trim() : primary;
    if (p) return primary;
    return fallback ?? null;
  };
  const pickConfidence = (primary, fallback) => {
    const p = Number(primary);
    if (Number.isFinite(p) && p > 0) return p;
    const f = Number(fallback);
    if (Number.isFinite(f) && f > 0) return f;
    return null;
  };
  const localById = new Map(
    (Array.isArray(localPhotos) ? localPhotos : [])
      .filter((p) => p && p.id)
      .map((p) => [String(p.id), p])
  );
  const byId = new Map((Array.isArray(jobPhotos) ? jobPhotos : []).map((p) => [String(p.id), p]));
  const ordered = [];

  (Array.isArray(photoIds) ? photoIds : []).forEach((id, index) => {
    const key = String(id || "");
    if (!key) return;
    const row = byId.get(key);
    const prior = localById.get(key) || {};
    if (!row) return;
    ordered.push({
      ...prior,
      ...row,
      room: pickBackendText(row, prior, "room"),
      damage_type: pickBackendText(row, prior, "damage_type"),
      description: stripTextureFromDescription(
        stripRoomFromDescription(
          pickBackendText(row, prior, "room"),
          pickBackendText(row, prior, "description")
        )
      ),
      confidence: pickConfidence(row.confidence, prior.confidence),
      thumbnail_url: localPhotos[index]?.thumbnail_url || "",
      analysis_source: pickText(row.analysis_source, prior.analysis_source),
      analysis_error: pickText(row.analysis_error, prior.analysis_error),
    });
    byId.delete(key);
  });

  for (const row of byId.values()) {
    const prior = localById.get(String(row.id)) || {};
    ordered.push({
      ...prior,
      ...row,
      room: pickBackendText(row, prior, "room"),
      damage_type: pickBackendText(row, prior, "damage_type"),
      description: stripTextureFromDescription(
        stripRoomFromDescription(
          pickBackendText(row, prior, "room"),
          pickBackendText(row, prior, "description")
        )
      ),
      confidence: pickConfidence(row.confidence, prior.confidence),
      thumbnail_url: prior.thumbnail_url || "",
      analysis_source: pickText(row.analysis_source, prior.analysis_source),
      analysis_error: pickText(row.analysis_error, prior.analysis_error),
    });
  }
  return ordered;
}

async function copyText(value) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const el = document.createElement("textarea");
  el.value = value;
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  el.remove();
}

export default function BuildWizard() {
  const [state, setState] = React.useState(() => safeLoadState());
  const [photoFiles, setPhotoFiles] = React.useState([]);
  const [guidelineFile, setGuidelineFile] = React.useState(null);
  const [sketchFile, setSketchFile] = React.useState(null);
  const [copiedLabelIndex, setCopiedLabelIndex] = React.useState(-1);
  const [photoEditDrafts, setPhotoEditDrafts] = React.useState({});
  const [photoSaveState, setPhotoSaveState] = React.useState({});
  const [photoBlobUrls, setPhotoBlobUrls] = React.useState({});
  const [previewImage, setPreviewImage] = React.useState(null);
  const [flash, setFlash] = React.useState({ type: "", text: "" });
  const [packCheckRetrying, setPackCheckRetrying] = React.useState(false);
  const [publicSafetyContacts, setPublicSafetyContacts] = React.useState([]);
  const [publicSafetyLoading, setPublicSafetyLoading] = React.useState(false);
  const [publicSafetyError, setPublicSafetyError] = React.useState("");
  const [publicSafetyResolvedLocation, setPublicSafetyResolvedLocation] = React.useState("");
  const [canUseLineItemSuggestions, setCanUseLineItemSuggestions] = React.useState(null);
  const [labelIntakeBusy, setLabelIntakeBusy] = React.useState(false);
  const [labelIntakeMessage, setLabelIntakeMessage] = React.useState("");
  const [dragPhotoId, setDragPhotoId] = React.useState("");
  const [dragOverPhotoId, setDragOverPhotoId] = React.useState("");
  const [selectedPhotoDragIndex, setSelectedPhotoDragIndex] = React.useState(null);
  const [availableJobs, setAvailableJobs] = React.useState([]);
  const [targetJobId, setTargetJobId] = React.useState("");
  const [linkingJob, setLinkingJob] = React.useState(false);

  const photosInputRef = React.useRef(null);
  const guidelineInputRef = React.useRef(null);
  const sketchInputRef = React.useRef(null);
  const selectedPhotoGridRef = React.useRef(null);
  const selectedPhotoDragScrollFrameRef = React.useRef(0);
  const advanceTimerRef = React.useRef(null);
  const runActiveRef = React.useRef(0);
  const processingRunStartedRef = React.useRef(false);
  const uploadRunRef = React.useRef(0);
  const photoBlobUrlsRef = React.useRef({});
  const lastJobIdRef = React.useRef("");
  const photoAutosaveTimersRef = React.useRef({});

  const refreshJobPhotos = React.useCallback(
    async (jobId, { silent = true } = {}) => {
      if (!jobId) return;
      try {
        const listed = await listJobPhotos(jobId);
        const listedPhotos = Array.isArray(listed?.photos) ? listed.photos : [];
        setState((prev) => {
          const refreshed = mergePhotosWithPreviews(
            prev.photoIds,
            listedPhotos,
            Array.isArray(prev.jobPhotos) && prev.jobPhotos.length > 0 ? prev.jobPhotos : prev.photos
          );
          const normalized = normalizeRoomsUnique(refreshed, {
            preferSketch: Boolean(prev.sketchDocumentId || prev.sketch),
          });
          return { ...prev, jobPhotos: normalized, photoIds: normalized.map((p) => p.id).filter(Boolean) };
        });
      } catch (error) {
        if (!silent) {
          setState((prev) => ({
            ...prev,
            photoSyncError: `Photo list sync failed: ${error?.message || "Unknown error"}`,
          }));
        }
      }
    },
    []
  );

  React.useEffect(() => {
    let active = true;
    getBillingSummary()
      .then((summary) => {
        if (active) setCanUseLineItemSuggestions(Boolean(summary?.feature_access));
      })
      .catch(() => {
        if (active) setCanUseLineItemSuggestions(false);
      });
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    const payload = sanitizeBuildStateForStorage(state);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      try {
        const fallback = sanitizeBuildStateForStorage(state, { compact: true });
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fallback));
      } catch (fallbackError) {
        const cause = isStorageQuotaError(error) || isStorageQuotaError(fallbackError) ? "quota" : "write";
        console.warn(`[BuildWizard] sessionStorage persistence skipped (${cause})`, fallbackError);
      }
    }
  }, [state]);

  React.useEffect(() => {
    if (!flash.text) return undefined;
    const id = window.setTimeout(() => setFlash({ type: "", text: "" }), 1800);
    return () => window.clearTimeout(id);
  }, [flash]);

  React.useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch (_e) {
      window.scrollTo(0, 0);
    }
  }, [state.currentStepId]);

  React.useEffect(() => {
    if (!previewImage) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setPreviewImage(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewImage]);

  React.useEffect(() => {
    if (lastJobIdRef.current && lastJobIdRef.current !== state.jobId) {
      Object.values(photoBlobUrlsRef.current).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_e) {
          // ignore
        }
      });
      photoBlobUrlsRef.current = {};
      setPhotoBlobUrls({});
    }
    lastJobIdRef.current = state.jobId || "";
  }, [state.jobId]);

  React.useEffect(() => {
    let cancelled = false;
    const rows = Array.isArray(state.jobPhotos) ? state.jobPhotos : [];
    rows.forEach((photo) => {
      const photoId = String(photo?.id || "");
      const storageUrl = String(photo?.storage_url || "").trim();
      if (!photoId || !storageUrl || photoBlobUrlsRef.current[photoId]) return;
      fetchJobPhotoBlobUrl(state.jobId, photoId, storageUrl)
        .then((blobUrl) => {
          if (!blobUrl) return;
          if (cancelled) {
            try {
              URL.revokeObjectURL(blobUrl);
            } catch (_e) {
              // ignore
            }
            return;
          }
          photoBlobUrlsRef.current[photoId] = blobUrl;
          setPhotoBlobUrls((prev) => ({ ...prev, [photoId]: blobUrl }));
        })
        .catch(() => {
          // keep local preview fallback
        });
    });
    return () => {
      cancelled = true;
    };
  }, [state.jobId, state.jobPhotos]);

  React.useEffect(
    () => () => {
      Object.values(photoBlobUrlsRef.current).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_e) {
          // ignore
        }
      });
      photoBlobUrlsRef.current = {};
    },
    []
  );

  React.useEffect(
    () => () => {
      Object.values(photoAutosaveTimersRef.current).forEach((timerId) => window.clearTimeout(timerId));
      photoAutosaveTimersRef.current = {};
    },
    []
  );

  React.useEffect(() => {
    if (!state.jobId) return;
    try {
      const raw = localStorage.getItem(`build_job_info:${state.jobId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.jobInfo || typeof parsed.jobInfo !== "object") return;
      setState((prev) => ({
        ...prev,
        jobInfo: {
          ...prev.jobInfo,
          ...parsed.jobInfo,
          savedAt: parsed.updatedAt || prev.jobInfo?.savedAt || "",
        },
      }));
    } catch (_error) {
      // ignore malformed local job metadata
    }
  }, [state.jobId]);

  const showFlash = React.useCallback((text, type = "success") => {
    setFlash({ type, text });
  }, []);

  const generateLineItemsFromApprovedLabels = React.useCallback(async () => {
    if (!state.jobId) {
      throw new Error("A job is required before creating line item suggestions.");
    }
    if (canUseLineItemSuggestions === false) {
      throw new Error("Line item suggestions are available on paid plans.");
    }
    if (state.guidelineDocumentId) {
      await attachGuidelineToJob(state.jobId, {
        platform: state.platform || "xactimate",
        profileId: "default",
        documentId: state.guidelineDocumentId,
      });
    }

    let sourcePhotos = Array.isArray(state.jobPhotos) ? state.jobPhotos : [];
    try {
      const listed = await listJobPhotos(state.jobId);
      if (Array.isArray(listed?.photos) && listed.photos.length > 0) {
        sourcePhotos = mergePhotosWithPreviews(state.photoIds, listed.photos, state.photos);
      }
    } catch (_e) {
      // use current in-memory photos if listing fails
    }

    const normalizedPhotos = normalizeRoomsUnique(sourcePhotos, {
      preferSketch: Boolean(state.sketchDocumentId || state.sketch),
    });
    const hasSketchForQuantities = Boolean(state.sketchDocumentId);
    const observations = buildObservationsFromJobPhotos(normalizedPhotos, {
      includeMeasurements: hasSketchForQuantities,
    });
    if (observations.length < 1) {
      throw new Error("No approved photo labels were found. Go back to Label Review and confirm the photo labels.");
    }

    const runResult = await runBuild(state.jobId, {
      platform: state.platform || "xactimate",
      profile_id: "default",
      observations,
      guideline_document_id: state.guidelineDocumentId || undefined,
      sketch_document_id: state.sketchDocumentId || undefined,
      photo_ids: state.photoIds,
      code_upgrades_enabled: Boolean(state.wantsCodeUpgrades),
      property_zip: state.wantsCodeUpgrades ? String(state.propertyZip || "").trim() : undefined,
      loss_cause: state.lossType || undefined,
    });

    const setId = runResult?.setId || "";
    const lineItems = normalizeLineItems(runResult?.raw?.items, { includeValues: hasSketchForQuantities });
    setState((prev) => ({
      ...prev,
      jobPhotos: normalizedPhotos,
      setId,
      lineItems,
      packRef: setId ? { kind: "set", id: setId } : null,
      buildRunRequested: true,
      scopeProgress: 100,
      packageProgress: 100,
    }));
    return { setId, lineItems };
  }, [
    state.jobId,
    state.guidelineDocumentId,
    state.platform,
    state.jobPhotos,
    state.photoIds,
    state.photos,
    state.sketchDocumentId,
    state.sketch,
    state.wantsCodeUpgrades,
    state.propertyZip,
    state.lossType,
    canUseLineItemSuggestions,
  ]);

  React.useEffect(() => {
    if (state.currentStepId !== STEP_IDS.A5) {
      processingRunStartedRef.current = false;
      return;
    }
    if (state.processingDone) return;
    if (state.processingError) return;
    // Photo polling updates state while this effect is running. Do not restart
    // the same build and reset its progress when those updates arrive.
    if (processingRunStartedRef.current) return;
    runActiveRef.current += 1;
    const runId = runActiveRef.current;

    const run = async () => {
      const setSafe = (updater) => {
        if (runActiveRef.current !== runId) return;
        setState(updater);
      };
      try {
        if (state.uploadingPhotos) {
          setSafe((prev) => ({
            ...prev,
            processingPhase: "Uploading",
            processingProgress: Math.max(10, Math.min(99, Number(prev.uploadProgress) || 10)),
            processingMessage: "Photos are still uploading. Processing will continue automatically when upload finishes.",
          }));
          return;
        }
        processingRunStartedRef.current = true;
        if (
          state.wantsPhotoLabels === true &&
          state.wantsLineItemSuggestions === true &&
          canUseLineItemSuggestions !== false
        ) {
          await refreshJobPhotos(state.jobId, { silent: true });
          setSafe((prev) => ({
            ...prev,
            packReady: false,
            processingDone: true,
            currentStepId: STEP_IDS.A6,
            processingError: "",
            processingPhase: "Ready",
            processingProgress: 100,
            scopeProgress: 0,
            packageProgress: 0,
            processingMessage: "Photo labels are ready. Review and save labels before creating line item suggestions.",
          }));
          advanceTimerRef.current = window.setTimeout(() => {
            if (runActiveRef.current !== runId) return;
            setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }));
          }, 600);
          return;
        }
        if (state.wantsLineItemSuggestions === false || canUseLineItemSuggestions === false) {
          if (state.wantsPhotoLabels) {
            await refreshJobPhotos(state.jobId, { silent: true });
          }
          setSafe((prev) => ({
            ...prev,
            packReady: false,
            processingDone: true,
            currentStepId: STEP_IDS.A6,
            processingError: "",
            processingPhase: "Ready",
            processingProgress: 100,
            scopeProgress: 100,
            packageProgress: 100,
            processingMessage:
              state.wantsLineItemSuggestions === false
                ? state.wantsPhotoLabels
                  ? "Photo labels are ready. Line item suggestions were skipped."
                  : "Line item suggestions were skipped."
                : "Photo labels are ready. Line item suggestions require a paid plan.",
          }));
          advanceTimerRef.current = window.setTimeout(() => {
            if (runActiveRef.current !== runId) return;
            setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }));
          }, 600);
          return;
        }
        const shouldSubmitRun = true;
        if (shouldSubmitRun) {
          if (state.guidelineDocumentId) {
            setSafe((prev) => ({
              ...prev,
              processingPhase: "Running",
              processingMessage: "Binding guideline to this job",
              processingProgress: 40,
              scopeProgress: 20,
            }));
            await attachGuidelineToJob(state.jobId, {
              platform: state.platform || "xactimate",
              profileId: "default",
              documentId: state.guidelineDocumentId,
            });
          }
          setSafe((prev) => ({ ...prev, buildRunRequested: true, processingError: "", scopeProgress: 30 }));
          let sourcePhotos = Array.isArray(state.jobPhotos) ? state.jobPhotos : [];
          try {
            const listed = await listJobPhotos(state.jobId);
            if (Array.isArray(listed?.photos) && listed.photos.length > 0) {
              sourcePhotos = mergePhotosWithPreviews(state.photoIds, listed.photos, state.photos);
            }
          } catch (_e) {
            // use current in-memory photos if listing fails
          }

          const normalizedPhotos = normalizeRoomsUnique(sourcePhotos, {
            preferSketch: Boolean(state.sketchDocumentId || state.sketch),
          });
          const hasSketchForQuantities = Boolean(state.sketchDocumentId);
          const observations = buildObservationsFromJobPhotos(normalizedPhotos, {
            includeMeasurements: hasSketchForQuantities,
          });
          if (observations.length < 1) {
            throw new Error("No backend photo rows found. Go back to Photos Upload and upload photos again.");
          }
          setSafe((prev) => ({
            ...prev,
            jobPhotos: normalizedPhotos,
            processingPhase: "Running",
            processingMessage: "Submitting build request",
            processingProgress: 45,
            scopeProgress: 55,
          }));

          const runResult = await runBuild(state.jobId, {
            platform: state.platform || "xactimate",
            profile_id: "default",
            observations,
            guideline_document_id: state.guidelineDocumentId || undefined,
            sketch_document_id: state.sketchDocumentId || undefined,
            photo_ids: state.photoIds,
            code_upgrades_enabled: Boolean(state.wantsCodeUpgrades),
            property_zip: state.wantsCodeUpgrades ? String(state.propertyZip || "").trim() : undefined,
            loss_cause: state.lossType || undefined,
          });

          const setId = runResult?.setId || "";
          const lineItems = normalizeLineItems(runResult?.raw?.items, { includeValues: hasSketchForQuantities });
          setSafe((prev) => ({
            ...prev,
            setId,
            lineItems,
            packRef: setId ? { kind: "set", id: setId } : null,
            buildRunRequested: true,
            scopeProgress: 100,
          }));
        }

        setSafe((prev) => ({
          ...prev,
          processingPhase: "Packaging",
          processingMessage: "Checking estimator pack availability",
          processingProgress: 70,
          packageProgress: 0,
        }));
        const packStatus = await pollEstimatorPackReady(state.jobId, {
          platform: state.platform || "xactimate",
          include_all: true,
          include_rationale: true,
          optimize_entry_order: 1,
          maxAttempts: 60,
          intervalMs: 1000,
          onProgress: (progress) => {
            setSafe((prev) => ({
              ...prev,
              processingPhase: progress.phase || "Packaging",
              processingProgress: Math.max(70, Math.min(99, Number(progress.progress) || 70)),
              packageProgress: Math.max(1, Math.min(99, Number(progress.progress) || 1)),
              processingMessage:
                progress.message ||
                `Checking estimator pack (${progress.attempt || 0}/${progress.maxAttempts || 60})`,
            }));
          },
        });
        if (!packStatus.ready) {
          if (packStatus.error) {
            throw new Error(packStatus.error);
          }
          const latestFallback = await getBuildStatus(state.jobId, {
            platform: state.platform || "xactimate",
          });
          const fallbackItems = normalizeLineItems(latestFallback?.raw?.items, {
            includeValues: Boolean(state.sketchDocumentId),
          });
          setSafe((prev) => ({
            ...prev,
            lineItems: fallbackItems.length > 0 ? fallbackItems : prev.lineItems,
            packRef: { kind: "job", id: state.jobId },
            packReady: false,
            processingDone: true,
            currentStepId: STEP_IDS.A6,
            processingError: "",
            processingPhase: "Ready",
            processingProgress: 100,
            packageProgress: 100,
            processingMessage:
              "Processing finished, but estimator pack is not ready yet. Continue and retry pack check from review.",
          }));
          advanceTimerRef.current = window.setTimeout(() => {
            if (runActiveRef.current !== runId) return;
            setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }));
          }, 600);
          return;
        }
        await refreshJobPhotos(state.jobId, { silent: true });
        const latest = await getBuildStatus(state.jobId, { platform: state.platform || "xactimate" });
        const latestItems = normalizeLineItems(latest?.raw?.items, { includeValues: Boolean(state.sketchDocumentId) });
        setSafe((prev) => ({
          ...prev,
          lineItems: latestItems.length > 0 ? latestItems : prev.lineItems,
          packRef: packStatus.ref || { kind: "job", id: state.jobId },
        }));

        setSafe((prev) => ({
          ...prev,
          packReady: true,
          processingDone: true,
          currentStepId: STEP_IDS.A6,
          processingPhase: "Ready",
          processingProgress: 100,
          packageProgress: 100,
          processingMessage: "Estimator pack is ready",
        }));

        advanceTimerRef.current = window.setTimeout(() => {
          if (runActiveRef.current !== runId) return;
          setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }));
        }, 600);
      } catch (error) {
        const message = error?.message || "Build failed";
        if (state.wantsPhotoLabels && isFreePlanScopeGateError(message)) {
          await refreshJobPhotos(state.jobId, { silent: true });
          setSafe((prev) => ({
            ...prev,
            packReady: false,
            processingDone: true,
            currentStepId: STEP_IDS.A6,
            processingError: "",
            processingPhase: "Ready",
            processingProgress: 100,
            scopeProgress: 100,
            packageProgress: 100,
            processingMessage:
              "Photo labels are ready. Scope suggestions and estimator-pack export require a paid plan.",
          }));
          advanceTimerRef.current = window.setTimeout(() => {
            if (runActiveRef.current !== runId) return;
            setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }));
          }, 600);
          return;
        }
        setSafe((prev) => ({
          ...prev,
          processingError: message,
          processingDone: false,
          processingPhase: "Packaging",
          processingProgress: 0,
          processingMessage: message,
        }));
      }
    };

    run();

    return () => {
      runActiveRef.current += 1;
      if (advanceTimerRef.current) {
        window.clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [
    state.currentStepId,
    state.guidelineDocumentId,
    state.jobId,
    state.platform,
    state.propertyZip,
    state.sketchDocumentId,
    state.uploadingPhotos,
    state.wantsCodeUpgrades,
    state.wantsLineItemSuggestions,
    state.wantsPhotoLabels,
    canUseLineItemSuggestions,
    refreshJobPhotos,
  ]);

  React.useEffect(() => {
    if (!state.jobId) return;
    if (state.currentStepId !== STEP_IDS.A5 && state.currentStepId !== STEP_IDS.A6) return;
    let active = true;
    const tick = async () => {
      if (!active) return;
      await refreshJobPhotos(state.jobId, { silent: true });
    };
    tick();
    const intervalId = window.setInterval(tick, 3000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [state.currentStepId, state.jobId, refreshJobPhotos]);

  React.useEffect(() => {
    if (state.currentStepId !== STEP_IDS.A7) return;
    if (!state.jobId) return;
    let active = true;
    const loadLatest = async () => {
      setState((prev) => ({ ...prev, lineItemsLoading: true, lineItemsConfirmedEmpty: false }));
      try {
        const platform = state.platform || "xactimate";
        if (
          state.wantsPhotoLabels === true &&
          state.wantsLineItemSuggestions === true &&
          state.buildRunRequested !== true
        ) {
          const generated = await generateLineItemsFromApprovedLabels();
          if (!active) return;
          setState((prev) => ({
            ...prev,
            lineItems: Array.isArray(generated?.lineItems) ? generated.lineItems : prev.lineItems,
            lineItemsLoading: false,
            lineItemsConfirmedEmpty: !Array.isArray(generated?.lineItems) || generated.lineItems.length < 1,
          }));
          return;
        }

        const latest = await getBuildStatus(state.jobId, { platform });
        if (!active) return;
        let items = normalizeLineItems(latest?.raw?.items, { includeValues: Boolean(state.sketchDocumentId) });
        if (items.length < 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 500));
          if (!active) return;
          const latestRetry = await getBuildStatus(state.jobId, { platform });
          if (!active) return;
          items = normalizeLineItems(latestRetry?.raw?.items, { includeValues: Boolean(state.sketchDocumentId) });
        }
        if (items.length > 0) {
          setState((prev) => ({
            ...prev,
            lineItems: items,
            lineItemsLoading: false,
            lineItemsConfirmedEmpty: false,
          }));
        } else {
          const fallback = buildFallbackLineItemsFromPhotos(state.jobPhotos, state.platform || "xactimate", {
            includeValues: Boolean(state.sketchDocumentId),
          });
          setState((prev) => ({
            ...prev,
            lineItems: fallback,
            lineItemsLoading: false,
            lineItemsConfirmedEmpty: fallback.length < 1,
          }));
          if (fallback.length > 0) {
            showFlash("No exact matches from engine. Showing provisional line items from photo labels.", "success");
          }
        }
      } catch (_e) {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          lineItemsLoading: false,
          lineItemsConfirmedEmpty: true,
        }));
      }
    };
    loadLatest();
    return () => {
      active = false;
    };
  }, [
    state.currentStepId,
    state.jobId,
    state.platform,
    state.sketchDocumentId,
    state.wantsPhotoLabels,
    state.wantsLineItemSuggestions,
    state.buildRunRequested,
    generateLineItemsFromApprovedLabels,
  ]);

  React.useEffect(() => {
    if (state.currentStepId !== STEP_IDS.A2) return;
    let active = true;
    const loadGuidelines = async () => {
      try {
        const res = await listGuidelines();
        if (!active) return;
        const docs = Array.isArray(res?.guidelines) ? res.guidelines : [];
        setState((prev) => ({
          ...prev,
          availableGuidelines: docs,
          selectedGuidelineId:
            prev.selectedGuidelineId ||
            (prev.guidelineDocumentId && docs.some((d) => d.id === prev.guidelineDocumentId)
              ? prev.guidelineDocumentId
              : ""),
        }));
      } catch (_e) {
        if (!active) return;
        setState((prev) => ({ ...prev, availableGuidelines: [] }));
      }
    };
    loadGuidelines();
    return () => {
      active = false;
    };
  }, [state.currentStepId, state.guidelineDocumentId, state.selectedGuidelineId]);

  React.useEffect(() => {
    if (state.currentStepId !== STEP_IDS.A6) return;
    let active = true;
    listExistingJobs({ limit: 300, offset: 0 })
      .then((rows) => {
        if (!active) return;
        setAvailableJobs(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (active) setAvailableJobs([]);
      });
    return () => {
      active = false;
    };
  }, [state.currentStepId]);

  React.useEffect(() => {
    const normalizedLoss = String(state.lossType || "").toLowerCase();
    const shouldLookup =
      state.currentStepId === STEP_IDS.A6 && CIVIC_CONTACT_LOSS_TYPES.has(normalizedLoss);

    if (!shouldLookup) {
      setPublicSafetyContacts([]);
      setPublicSafetyError("");
      setPublicSafetyLoading(false);
      setPublicSafetyResolvedLocation("");
      return;
    }

    let active = true;
    const run = async () => {
      try {
        setPublicSafetyLoading(true);
        setPublicSafetyError("");
        const result = await lookupPublicSafetyContacts({
          lossType: normalizedLoss,
          zipCode: String(state.propertyZip || "").trim(),
          address: String(state.jobInfo?.propertyAddress || "").trim(),
        });
        if (!active) return;
        setPublicSafetyContacts(Array.isArray(result?.contacts) ? result.contacts : []);
        setPublicSafetyResolvedLocation(String(result?.resolved_location || "").trim());
      } catch (e) {
        if (!active) return;
        setPublicSafetyContacts([]);
        setPublicSafetyError(e?.message || "Could not load public safety contacts.");
      } finally {
        if (active) setPublicSafetyLoading(false);
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [state.currentStepId, state.lossType, state.propertyZip, state.jobInfo?.propertyAddress]);

  const setProcessingPhase = (phase, message) => {
    const progressByPhase = {
      "Creating job": 10,
      Uploading: 25,
      Running: 45,
      Packaging: 70,
      Ready: 100,
    };
    setState((prev) => ({
      ...prev,
      processingPhase: phase,
      processingMessage: message || "",
      processingProgress: progressByPhase[phase] ?? prev.processingProgress ?? 0,
    }));
  };

  const handlePhotosChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    const previewUrls = await Promise.all(files.map((file) => readFileAsDataUrl(file)));
    const nextFiles = [...photoFiles, ...files];
    setPhotoFiles(nextFiles);
    setPhotoEditDrafts({});
    setPhotoSaveState({});
    setState((prev) => ({
      ...prev,
      photos: [
        ...prev.photos,
        ...files.map((file, index) => ({ ...toFileMeta(file), thumbnail_url: previewUrls[index] || "" })),
      ],
      jobPhotos: [],
      photoIds: [],
      photoSyncError: "",
      wantsPhotoLabels: null,
      wantsLineItemSuggestions: null,
      lossType: "",
      wantsCodeUpgrades: null,
      propertyZip: "",
      uploadProgress: 0,
      uploadStatusText: "",
      guidelineProgress: 0,
      sketchProgress: 0,
      scopeProgress: 0,
      packageProgress: 0,
      processingError: "",
    }));
  };

  const reorderArrayByIndex = (rows, fromIndex, toIndex) => {
    if (!Array.isArray(rows)) return rows;
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= rows.length || toIndex >= rows.length || fromIndex === toIndex) {
      return rows;
    }
    const nextRows = [...rows];
    const [moved] = nextRows.splice(fromIndex, 1);
    nextRows.splice(toIndex, 0, moved);
    return nextRows;
  };

  const handleSelectedPhotoMove = (fromIndex, toIndex) => {
    const photoCount = Array.isArray(state.photos) ? state.photos.length : 0;
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= photoCount || toIndex >= photoCount || fromIndex === toIndex) return;
    setPhotoFiles((prev) => reorderArrayByIndex(prev, fromIndex, toIndex));
    setState((prev) => ({
      ...prev,
      photos: reorderArrayByIndex(prev.photos, fromIndex, toIndex),
      jobPhotos: [],
      photoIds: [],
      photoSyncError: "",
      processingError: "",
      uploadProgress: 0,
      uploadStatusText: "",
    }));
  };

  const handleSelectedPhotoDelete = async (index) => {
    const currentPhotos = Array.isArray(state.photos) ? state.photos : [];
    if (index < 0 || index >= currentPhotos.length) return;
    const photo = currentPhotos[index];
    const photoId = String(photo?.id || state.photoIds[index] || "").trim();

    if (state.jobId && photoId) {
      try {
        await deleteJobPhotos(state.jobId, [photoId]);
      } catch (error) {
        const message = error?.message || "Could not delete photo.";
        setState((prev) => ({ ...prev, processingError: message, photoSyncError: message }));
        showFlash(message, "error");
        return;
      }
    }

    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
    setState((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
      jobPhotos: prev.jobPhotos.filter((p, i) => i !== index && (!photoId || String(p?.id || "") !== photoId)),
      photoIds: prev.photoIds.filter((id, i) => i !== index && (!photoId || String(id || "") !== photoId)),
      photoSyncError: "",
      processingError: "",
      processingDone: false,
      packReady: false,
      packRef: null,
      buildRunRequested: false,
    }));
    showFlash("Photo deleted", "success");
  };

  const handleSelectedPhotoDrop = (targetIndex) => {
    const fromIndex = Number(selectedPhotoDragIndex);
    setSelectedPhotoDragIndex(null);
    stopSelectedPhotoDragScroll();
    if (!Number.isInteger(fromIndex)) return;
    handleSelectedPhotoMove(fromIndex, targetIndex);
  };

  const stopSelectedPhotoDragScroll = () => {
    if (selectedPhotoDragScrollFrameRef.current) {
      window.cancelAnimationFrame(selectedPhotoDragScrollFrameRef.current);
      selectedPhotoDragScrollFrameRef.current = 0;
    }
  };

  const handleSelectedPhotoGridDragOver = (event) => {
    if (selectedPhotoDragIndex === null) return;
    event.preventDefault();
    const grid = selectedPhotoGridRef.current || event.currentTarget;
    const pointerY = event.clientY;
    stopSelectedPhotoDragScroll();
    selectedPhotoDragScrollFrameRef.current = window.requestAnimationFrame(() => {
      selectedPhotoDragScrollFrameRef.current = 0;
      const bounds = grid.getBoundingClientRect();
      const gridEdgeSize = Math.min(130, Math.max(64, bounds.height * 0.2));
      const viewportEdgeSize = Math.min(150, Math.max(72, window.innerHeight * 0.16));
      let gridDelta = 0;
      let windowDelta = 0;

      if (pointerY - bounds.top < gridEdgeSize) {
        gridDelta = -32;
      } else if (bounds.bottom - pointerY < gridEdgeSize) {
        gridDelta = 32;
      }

      if (pointerY < viewportEdgeSize) {
        windowDelta = -34;
      } else if (window.innerHeight - pointerY < viewportEdgeSize) {
        windowDelta = 34;
      }

      if (gridDelta) grid.scrollBy({ top: gridDelta, behavior: "auto" });
      if (windowDelta) window.scrollBy({ top: windowDelta, behavior: "auto" });
    });
  };

  const handleGuidelineChange = (event) => {
    const fileObj = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setGuidelineFile(fileObj);
    setState((prev) => ({
      ...prev,
      guideline: fileObj ? toFileMeta(fileObj) : null,
      guidelineDocumentId: "",
      guidelineSkipped: !fileObj,
      processingError: "",
    }));
  };

  const handleSketchChange = (event) => {
    const fileObj = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setSketchFile(fileObj);
    setState((prev) => ({
      ...prev,
      sketch: fileObj ? toFileMeta(fileObj) : null,
      sketchDocumentId: "",
      sketchSkipped: !fileObj,
      processingError: "",
    }));
  };

  const startPhotoUploadInBackground = React.useCallback(
    async (jobId, filesToUpload, localPhotoCount = 0) => {
      const uploadRunId = uploadRunRef.current + 1;
      uploadRunRef.current = uploadRunId;
      const totalPhotos = Math.max(Number(localPhotoCount) || 0, Array.isArray(filesToUpload) ? filesToUpload.length : 0);

      setProcessingPhase("Uploading", "Uploading photos");
      setState((prev) => ({
        ...prev,
        jobId,
        uploadingPhotos: true,
        uploadProgress: 0,
        uploadStatusText: "",
        processingError: "",
        photoSyncError: "",
      }));

      try {
        let photoIds = [];
        let jobPhotos = [];
        let photoSyncError = "";

        if (Array.isArray(filesToUpload) && filesToUpload.length > 0) {
          const uploaded = await uploadPhotos(jobId, filesToUpload, {
            onProgress: (progress) => {
              if (uploadRunRef.current !== uploadRunId) return;
              const pct = typeof progress === "object" ? progress.percent : progress;
              const completed = Number(progress?.completed);
              const total = Number(progress?.total);
              const phase = String(progress?.phase || "");
              const safeTotal = Number.isFinite(total) && total > 0 ? total : totalPhotos;
              const current = Math.min(safeTotal || 1, Math.max(1, Number(progress?.current) || completed + 1 || 1));
              const statusText =
                phase === "finalizing"
                  ? `Uploaded ${current} of ${safeTotal || current} photos. Finalizing labels...`
                  : phase === "complete"
                    ? `${safeTotal || completed || current} of ${safeTotal || completed || current} photos uploaded and labeled`
                    : Number.isFinite(completed) && safeTotal > 0
                      ? `${Math.min(completed, safeTotal)} of ${safeTotal} photos uploaded`
                      : "";
              setState((prev) => ({
                ...prev,
                uploadProgress: clampPercent(pct),
                uploadStatusText: statusText,
              }));
            },
          });
          photoIds = Array.isArray(uploaded.photoIds) ? uploaded.photoIds : [];
          jobPhotos = Array.isArray(uploaded.photos) ? uploaded.photos : [];

          try {
            const listed = await listJobPhotos(jobId);
            jobPhotos = Array.isArray(listed?.photos) ? listed.photos : [];
            photoIds = jobPhotos.map((p) => p.id).filter(Boolean);

          } catch (e) {
            photoSyncError = `Photo list sync failed: ${e?.message || "Unknown error"}`;
          }
        } else {
          photoIds = Array.from({ length: totalPhotos }, (_, idx) => `photo_${idx + 1}`);
          photoSyncError = "Unable to sync photos from backend.";
        }

        if (uploadRunRef.current !== uploadRunId) return;
        if (jobPhotos.length < 1 && !photoSyncError) {
          photoSyncError = "Unable to sync photos from backend.";
        }

        setState((prev) => {
          const hydratedPhotos = normalizeRoomsUnique(
            mergePhotosWithPreviews(photoIds, jobPhotos, prev.photos),
            { preferSketch: Boolean(prev.sketchDocumentId || prev.sketch) }
          );
          const completedCount = photoIds.length || hydratedPhotos.length || totalPhotos;
          return {
            ...prev,
            jobId,
            photoIds,
            jobPhotos: hydratedPhotos,
            photoSyncError,
            processingError: "",
            uploadingPhotos: false,
            uploadProgress: 100,
            uploadStatusText: `${completedCount} of ${totalPhotos || completedCount} photos uploaded and labeled`,
          };
        });
        showFlash("Photos uploaded", "success");
      } catch (error) {
        if (uploadRunRef.current !== uploadRunId) return;
        const message = error?.message || "Photo upload failed";
        setState((prev) => ({
          ...prev,
          processingError: message,
          photoSyncError: message,
          uploadingPhotos: false,
          uploadStatusText: message,
        }));
      }
    },
    [showFlash]
  );

  const handleA0Continue = async () => {
    try {
      setProcessingPhase("Creating job", "Ensuring job exists");
      let jobId = state.jobId;
      if (!jobId) {
        const created = await createJob();
        jobId = created.jobId;
      }

      setState((prev) => ({
        ...prev,
        jobId,
        currentStepId: STEP_IDS.A1,
        processingError: "",
      }));
      if (!state.uploadingPhotos && state.photoIds.length < 1) {
        startPhotoUploadInBackground(jobId, photoFiles, state.photos.length);
      }
    } catch (error) {
      const message = error?.message || "Photo upload failed";
      setState((prev) => ({
        ...prev,
        processingError: message,
        photoSyncError: message,
        uploadingPhotos: false,
        uploadStatusText: message,
      }));
    }
  };

  const handleA1Continue = async () => {
    const lineItemsWanted = state.wantsLineItemSuggestions === true && canUseLineItemSuggestions !== false;
    const nextStepId = lineItemsWanted ? STEP_IDS.A2 : STEP_IDS.A5;

    if (!LOSS_TYPE_OPTIONS.includes(String(state.lossType || "").toLowerCase())) {
      setState((prev) => ({
        ...prev,
        processingError: "Select a type of loss to continue.",
      }));
      return;
    }
    if (state.wantsCodeUpgrades) {
      const zip = String(state.propertyZip || "").trim();
      if (!/^\d{5}(-\d{4})?$/.test(zip)) {
        setState((prev) => ({
          ...prev,
          processingError: "Enter a valid ZIP code (e.g., 12345) for code upgrades.",
        }));
        return;
      }
    }
    if (state.uploadingPhotos) {
      setState((prev) => ({
        ...prev,
        currentStepId: nextStepId,
        processingError: "",
      }));
      return;
    }
    let currentJobPhotos = Array.isArray(state.jobPhotos) ? state.jobPhotos : [];
    let currentPhotoIds = Array.isArray(state.photoIds) ? state.photoIds : [];
    if (currentJobPhotos.length < 1 && state.jobId) {
      try {
        const listed = await listJobPhotos(state.jobId);
        currentJobPhotos = Array.isArray(listed?.photos) ? listed.photos : [];
        currentPhotoIds = currentJobPhotos.map((p) => p.id).filter(Boolean);
        if (currentJobPhotos.length > 0) {
          const hydrated = normalizeRoomsUnique(
            mergePhotosWithPreviews(currentPhotoIds, currentJobPhotos, state.photos),
            { preferSketch: Boolean(state.sketchDocumentId || state.sketch) }
          );
          setState((prev) => ({
            ...prev,
            photoIds: currentPhotoIds,
            jobPhotos: hydrated,
            photoSyncError: "",
          }));
          currentJobPhotos = hydrated;
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          processingError: `Photo list sync failed: ${error?.message || "Unknown error"}`,
          photoSyncError: `Photo list sync failed: ${error?.message || "Unknown error"}`,
        }));
        return;
      }
    }
    if (currentJobPhotos.length < 1) {
      setState((prev) => ({
        ...prev,
        processingError: "No uploaded photo rows were found yet. Wait for the upload to finish or re-upload the photos.",
        photoSyncError: "No uploaded photo rows were found yet. Wait for the upload to finish or re-upload the photos.",
      }));
      return;
    }

    const selectedDamageType = damageTypeFromLossType(state.lossType);
    const patchedPhotoIds = new Set();
    let updatedJobPhotos = currentJobPhotos.map((photo) => {
          if (String(photo?.analysis_source || "").toLowerCase() === "manual_user") return photo;
          if (!photo?.id || !selectedDamageType || !isUnknownDamageTypeValue(photo.damage_type)) return photo;
          patchedPhotoIds.add(String(photo.id));
          return { ...photo, damage_type: selectedDamageType };
        })
      ;

    if (patchedPhotoIds.size > 0 && state.jobId) {
      await Promise.allSettled(
        updatedJobPhotos
          .filter((photo) => patchedPhotoIds.has(String(photo?.id || "")))
          .map((photo) => patchJobPhoto(state.jobId, photo.id, { damage_type: selectedDamageType }))
      );
      try {
        const listed = await listJobPhotos(state.jobId);
        if (Array.isArray(listed?.photos) && listed.photos.length > 0) {
          updatedJobPhotos = normalizeRoomsUnique(
            mergePhotosWithPreviews(currentPhotoIds, listed.photos, state.photos),
            { preferSketch: Boolean(state.sketchDocumentId || state.sketch) }
          );
        }
      } catch (_e) {
        // Keep the local fallback labels if the refresh fails.
      }
    }

    setState((prev) => ({
      ...prev,
      jobPhotos: updatedJobPhotos.length > 0 ? updatedJobPhotos : prev.jobPhotos,
      currentStepId: nextStepId,
      platform: lineItemsWanted ? prev.platform : "",
      guideline: lineItemsWanted ? prev.guideline : null,
      guidelineDocumentId: lineItemsWanted ? prev.guidelineDocumentId : "",
      guidelineSkipped: lineItemsWanted ? prev.guidelineSkipped : true,
      selectedGuidelineId: lineItemsWanted ? prev.selectedGuidelineId : "",
      sketch: lineItemsWanted ? prev.sketch : null,
      sketchDocumentId: lineItemsWanted ? prev.sketchDocumentId : "",
      sketchSkipped: lineItemsWanted ? prev.sketchSkipped : true,
      processingError: "",
    }));
  };

  const handleA2Continue = async () => {
    if (state.selectedGuidelineId) {
      setState((prev) => ({
        ...prev,
        guidelineDocumentId: state.selectedGuidelineId,
        guidelineSkipped: false,
        currentStepId: STEP_IDS.A3,
        guidelineProgress: 100,
        processingError: "",
      }));
      showFlash("Guideline selected", "success");
      return;
    }
    if (!state.guideline) {
      setState((prev) => ({
        ...prev,
        guidelineSkipped: true,
        guidelineDocumentId: "",
        currentStepId: STEP_IDS.A3,
        guidelineProgress: 100,
      }));
      return;
    }
    try {
      setProcessingPhase("Uploading", "Uploading guidelines");
      setState((prev) => ({ ...prev, guidelineProgress: 0 }));
      const uploaded = await uploadGuidelines(state.jobId, guidelineFile, {
        onProgress: (pct) => setState((prev) => ({ ...prev, guidelineProgress: clampPercent(pct) })),
      });
      const guidelineDocumentId = uploaded.guidelineDocumentId || `guideline_${state.guideline.name}`;
      let docs = state.availableGuidelines;
      try {
        const listed = await listGuidelines();
        docs = Array.isArray(listed?.guidelines) ? listed.guidelines : docs;
      } catch (_e) {
        // ignore
      }
      setState((prev) => ({
        ...prev,
        availableGuidelines: docs,
        selectedGuidelineId: guidelineDocumentId,
        guidelineDocumentId,
        guidelineSkipped: false,
        currentStepId: STEP_IDS.A3,
        guidelineProgress: 100,
        processingError: "",
      }));
      showFlash(uploaded?.reused ? "Guideline already exists. Reused saved copy." : "Guideline uploaded", "success");
    } catch (error) {
      const message = error?.message || "Guideline upload failed.";
      setState((prev) => ({ ...prev, processingError: message }));
      showFlash(message, "error");
      return;
    }
  };

  const handleDeleteSelectedGuideline = async () => {
    if (!state.selectedGuidelineId) return;
    try {
      await deleteGuideline(state.selectedGuidelineId);
      const listed = await listGuidelines();
      const docs = Array.isArray(listed?.guidelines) ? listed.guidelines : [];
      setState((prev) => ({
        ...prev,
        availableGuidelines: docs,
        selectedGuidelineId: "",
        guidelineDocumentId: prev.guidelineDocumentId === state.selectedGuidelineId ? "" : prev.guidelineDocumentId,
      }));
      showFlash("Guideline deleted", "success");
    } catch (error) {
      setState((prev) => ({ ...prev, processingError: error?.message || "Could not delete guideline" }));
    }
  };

  const skipGuideline = () => {
    if (guidelineInputRef.current) guidelineInputRef.current.value = "";
    setGuidelineFile(null);
      setState((prev) => ({
        ...prev,
        guideline: null,
        selectedGuidelineId: "",
        guidelineDocumentId: "",
        guidelineSkipped: true,
        guidelineProgress: 100,
        currentStepId: STEP_IDS.A3,
      }));
  };

  const handleA3Continue = () => {
    setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A4 }));
  };

  const handleA4Continue = async () => {
    if (!state.sketch) {
      setState((prev) => ({
        ...prev,
        sketchSkipped: true,
        sketchDocumentId: "",
        currentStepId: STEP_IDS.A5,
        buildRunRequested: false,
        processingDone: false,
        sketchProgress: 100,
      }));
      return;
    }
    try {
      setProcessingPhase("Uploading", "Uploading sketch");
      setState((prev) => ({ ...prev, sketchProgress: 0 }));
      const uploaded = await uploadSketch(state.jobId, sketchFile, {
        onProgress: (pct) => setState((prev) => ({ ...prev, sketchProgress: clampPercent(pct) })),
      });
      const sketchDocumentId = uploaded.sketchDocumentId || `sketch_${state.sketch.name}`;
      setState((prev) => ({
        ...prev,
        sketchDocumentId,
        sketchSkipped: false,
        currentStepId: STEP_IDS.A5,
        buildRunRequested: false,
        processingDone: false,
        sketchProgress: 100,
        processingError: "",
      }));
      showFlash("Sketch uploaded", "success");
    } catch (error) {
      setState((prev) => ({
        ...prev,
        sketchDocumentId: `sketch_${state.sketch?.name || "local"}`,
        currentStepId: STEP_IDS.A5,
        buildRunRequested: false,
        processingDone: false,
        sketchProgress: 100,
        processingError: error?.message || "Sketch upload failed. Continuing.",
      }));
    }
  };

  const skipSketch = () => {
    if (sketchInputRef.current) sketchInputRef.current.value = "";
    setSketchFile(null);
    setState((prev) => ({
      ...prev,
      sketch: null,
      sketchDocumentId: "",
      sketchSkipped: true,
      currentStepId: STEP_IDS.A5,
      buildRunRequested: false,
      processingDone: false,
      sketchProgress: 100,
      processingError: "",
    }));
  };

  const retryBuild = () => {
    processingRunStartedRef.current = false;
    setState((prev) => ({
      ...prev,
      buildRunRequested: false,
      processingError: "",
      processingDone: false,
      processingPhase: "Running",
      processingProgress: 35,
      processingMessage: "Retrying build",
      scopeProgress: 0,
      packageProgress: 0,
      packReady: false,
      packRef: null,
    }));
  };

  const handleStepJump = (stepId) => {
    if (
      stepId === STEP_IDS.A7 &&
      state.wantsPhotoLabels === true &&
      state.wantsLineItemSuggestions === true
    ) {
      const hasDrafts = Object.keys(photoEditDrafts || {}).length > 0;
      if (hasDrafts || labelCompleteness.remaining > 0) {
        setState((prev) => ({
          ...prev,
          currentStepId: STEP_IDS.A6,
          processingError: hasDrafts
            ? "Save pending label edits before creating line item suggestions."
            : "Complete room and description labels before creating line item suggestions.",
        }));
        return;
      }
    }
    setState((prev) => ({
      ...prev,
      currentStepId: stepId,
      processingError: "",
      ...(stepId === STEP_IDS.A5 ? { buildRunRequested: false, processingDone: false } : {}),
    }));
  };

  const handleCancelBuild = () => {
    runActiveRef.current += 1;
    if (photosInputRef.current) photosInputRef.current.value = "";
    if (guidelineInputRef.current) guidelineInputRef.current.value = "";
    if (sketchInputRef.current) sketchInputRef.current.value = "";
    setPhotoFiles([]);
    setGuidelineFile(null);
    setSketchFile(null);
    setCopiedLabelIndex(-1);
    setPhotoEditDrafts({});
    setPhotoSaveState({});
    setState(createInitialState());
  };

  const handleCopyLabel = async (label, index) => {
    try {
      await copyText(label);
      setCopiedLabelIndex(index);
      window.setTimeout(() => setCopiedLabelIndex(-1), 1000);
    } catch (e) {
      setState((prev) => ({
        ...prev,
        processingError: "Could not copy label to clipboard",
      }));
    }
  };

  const handleDownloadPack = async () => {
    try {
      setState((prev) => ({ ...prev, downloadingPack: true, processingError: "" }));
      const downloadRef = state.packRef || (state.jobId ? { kind: "job", id: state.jobId } : null);
      const blob = await downloadEstimatorPack(downloadRef, {
        platform: state.platform || "xactimate",
        includeAll: true,
        includeRationale: true,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimator-pack-${state.jobId || "job"}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showFlash("Estimator pack downloaded", "success");
    } catch (error) {
      setState((prev) => ({
        ...prev,
        processingError: error?.message || "Download failed",
      }));
    } finally {
      setState((prev) => ({ ...prev, downloadingPack: false }));
    }
  };

  const handleDownloadAnnotatedPhotos = async () => {
    try {
      setState((prev) => ({ ...prev, downloadingAnnotatedPhotos: true, processingError: "" }));
      const blob = await downloadAnnotatedPhotoZip(state.jobId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `annotated-photos-${state.jobId || "job"}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showFlash("Annotated photos downloaded", "success");
    } catch (error) {
      setState((prev) => ({
        ...prev,
        processingError: error?.message || "Annotated photo download failed",
      }));
    } finally {
      setState((prev) => ({ ...prev, downloadingAnnotatedPhotos: false }));
    }
  };

  const startAnotherBuild = () => {
    if (photosInputRef.current) photosInputRef.current.value = "";
    if (guidelineInputRef.current) guidelineInputRef.current.value = "";
    if (sketchInputRef.current) sketchInputRef.current.value = "";
    setPhotoFiles([]);
    setGuidelineFile(null);
    setSketchFile(null);
    setCopiedLabelIndex(-1);
    setPhotoEditDrafts({});
    setPhotoSaveState({});
    setState(createInitialState());
  };

  const handlePhotoDraftChange = (photoId, field, value) => {
    const draftValue = String(value ?? "");
    setPhotoEditDrafts((prev) => ({
      ...prev,
      [photoId]: {
        ...(prev[photoId] || {}),
        [field]: draftValue,
      },
    }));
    setState((prev) => ({
      ...prev,
      jobPhotos: prev.jobPhotos.map((p) => (String(p?.id || "") === String(photoId) ? { ...p, [field]: draftValue } : p)),
      setId: "",
      packRef: null,
      packReady: false,
      buildRunRequested: false,
    }));
    if (photoAutosaveTimersRef.current[photoId]) {
      window.clearTimeout(photoAutosaveTimersRef.current[photoId]);
      delete photoAutosaveTimersRef.current[photoId];
    }
  };

  const handleClearPhotoField = (event, photoId, field) => {
    const input = event.currentTarget?.parentElement?.querySelector("input, textarea");
    handlePhotoDraftChange(photoId, field, "");
    window.requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      try {
        input.setSelectionRange(0, 0);
      } catch (_e) {
        // Some input types do not support selection ranges.
      }
    });
  };

  const handleSavePhoto = async (photo) => {
    if (!photo?.id) return;
    const draft = photoEditDrafts[photo.id] || {};
    const roomValue = Object.prototype.hasOwnProperty.call(draft, "room") ? draft.room : photo.room;
    const damageTypeValue = Object.prototype.hasOwnProperty.call(draft, "damage_type") ? draft.damage_type : photo.damage_type;
    const descriptionValue = Object.prototype.hasOwnProperty.call(draft, "description") ? draft.description : photo.description;
    const payload = {
      room: roomValue,
      damage_type: damageTypeValue,
      description: descriptionValue,
    };
    setPhotoSaveState((prev) => ({
      ...prev,
      [photo.id]: { saving: true, error: "" },
    }));
    setState((prev) => ({
      ...prev,
      jobPhotos: prev.jobPhotos.map((p) => (p.id === photo.id ? { ...p, ...payload } : p)),
    }));
    try {
      const updated = await patchJobPhoto(state.jobId, photo.id, payload);
      setState((prev) => ({
        ...prev,
        jobPhotos: prev.jobPhotos.map((p) =>
          p.id === photo.id
            ? {
                ...p,
                ...updated,
                thumbnail_url: p.thumbnail_url || resolveThumbUrl(updated.storage_url),
              }
            : p
        ),
        setId: "",
        packRef: null,
        packReady: false,
        buildRunRequested: false,
      }));
      await refreshJobPhotos(state.jobId, { silent: true });
      setPhotoSaveState((prev) => ({
        ...prev,
        [photo.id]: { saving: false, error: "" },
      }));
      showFlash("Label saved", "success");
      setPhotoEditDrafts((prev) => {
        const next = { ...prev };
        delete next[photo.id];
        return next;
      });
    } catch (error) {
      setPhotoSaveState((prev) => ({
        ...prev,
        [photo.id]: { saving: false, error: error?.message || "Save failed" },
      }));
    }
  };

  const handleSaveAllPhotos = async () => {
    const rows = summaryPhotoRows.filter((p) => p?.id);
    for (const photo of rows) {
      // eslint-disable-next-line no-await-in-loop
      await handleSavePhoto(photo);
    }
    showFlash("All labels saved", "success");
  };

  const handleCopyLineItemDescription = async (item, index) => {
    try {
      const qty = item?.qty === "" || item?.qty == null ? "" : String(item.qty);
      const unit = String(item?.unit || "").trim();
      const description = String(item?.description || "").trim();
      await copyText([description, qty && unit ? `${qty} ${unit}` : qty || unit].filter(Boolean).join(" | "));
      setState((prev) => ({ ...prev, copiedCodeIndex: index }));
      window.setTimeout(() => {
        setState((prev) => ({ ...prev, copiedCodeIndex: -1 }));
      }, 1000);
    } catch (_e) {
      setState((prev) => ({ ...prev, processingError: "Could not copy line item suggestion to clipboard" }));
    }
  };

  const handleReanalyze = async (photo = null) => {
    if (photo?.id) {
      if (!state.jobId) {
        const msg = "Cannot re-analyze: missing job id.";
        setState((prev) => ({ ...prev, processingError: msg }));
        showFlash(msg, "error");
        return;
      }
      setPhotoSaveState((prev) => ({
        ...prev,
        [photo.id]: { ...(prev[photo.id] || {}), saving: true, reanalyzing: true, error: "" },
      }));
      showFlash("Re-analyzing photo...", "success");
      try {
        const analyzed = await reanalyzeJobPhoto(state.jobId, photo.id);
        if (!analyzed.room && !analyzed.damage_type && !analyzed.description) {
          throw new Error(
            analyzed.error ||
              "Vision did not return labels for this photo. Check backend vision configuration and try again."
          );
        }
        setState((prev) => ({
          ...prev,
          jobPhotos: prev.jobPhotos.map((p) =>
            p.id === photo.id
              ? {
                  ...p,
                  ...analyzed,
                  thumbnail_url: p.thumbnail_url || resolveThumbUrl(p.storage_url),
                }
              : p
          ),
          setId: "",
          packRef: null,
          packReady: false,
          buildRunRequested: false,
        }));
        setPhotoEditDrafts((prev) => {
          const next = { ...prev };
          delete next[photo.id];
          return next;
        });
        await refreshJobPhotos(state.jobId, { silent: true });
        setPhotoSaveState((prev) => ({
          ...prev,
          [photo.id]: { ...(prev[photo.id] || {}), saving: false, reanalyzing: false, error: "" },
        }));
        showFlash("Photo re-analyzed", "success");
      } catch (error) {
        const msg = error?.message || "Photo re-analysis failed";
        setState((prev) => ({ ...prev, processingError: msg }));
        setPhotoSaveState((prev) => ({
          ...prev,
          [photo.id]: { ...(prev[photo.id] || {}), saving: false, reanalyzing: false, error: msg },
        }));
        showFlash(msg, "error");
      }
      return;
    }

    setState((prev) => ({
      ...prev,
      currentStepId: STEP_IDS.A5,
      setId: "",
      packRef: null,
      buildRunRequested: false,
      processingDone: false,
      processingError: "",
      processingPhase: "Running",
      processingProgress: 35,
      processingMessage: "Re-analyzing results",
      packReady: false,
    }));
    showFlash("Re-analysis started", "success");
  };

  const handleRegenerateLineItems = () => {
    setState((prev) => ({
      ...prev,
      setId: "",
      packRef: null,
      lineItems: [],
      lineItemsConfirmedEmpty: false,
      buildRunRequested: false,
      packReady: false,
      processingError: "",
    }));
    showFlash("Line item regeneration started", "success");
  };

  const handleDeleteReviewedPhoto = async (photo) => {
    const photoId = String(photo?.id || "").trim();
    if (!state.jobId || !photoId) return;
    try {
      await deleteJobPhotos(state.jobId, [photoId]);
      setState((prev) => ({
        ...prev,
        photos: prev.photos.filter((p) => String(p?.id || "") !== photoId),
        jobPhotos: prev.jobPhotos.filter((p) => String(p?.id || "") !== photoId),
        photoIds: prev.photoIds.filter((id) => String(id || "") !== photoId),
        setId: "",
        packRef: null,
        packReady: false,
        buildRunRequested: false,
        processingError: "",
        photoSyncError: "",
      }));
      setPhotoEditDrafts((prev) => {
        const next = { ...prev };
        delete next[photoId];
        return next;
      });
      showFlash("Photo deleted", "success");
    } catch (error) {
      const msg = error?.message || "Could not delete photo.";
      setState((prev) => ({ ...prev, processingError: msg, photoSyncError: msg }));
      showFlash(msg, "error");
    }
  };

  const handleLinkPhotosToExistingJob = async () => {
    const target = String(targetJobId || "").trim();
    if (!state.jobId || !target || target === state.jobId) return;
    const ids = summaryPhotoRows.map((p) => String(p?.id || "")).filter(Boolean);
    if (ids.length < 1) return;
    try {
      setLinkingJob(true);
      const result = await relinkJobPhotos(state.jobId, target, ids);
      const photos = Array.isArray(result?.photos) ? result.photos : [];
      const selectedJob = availableJobs.find((job) => String(job?.id || "") === target);
      setState((prev) => ({
        ...prev,
        jobId: target,
        photoIds: photos.map((p) => p.id).filter(Boolean),
        jobPhotos: mergePhotosWithPreviews(photos.map((p) => p.id).filter(Boolean), photos, prev.photos),
        setId: "",
        packRef: null,
        packReady: false,
        buildRunRequested: false,
        photoSyncError: "",
        processingError: "",
        jobInfo: {
          ...(prev.jobInfo || {}),
          jobName: prev.jobInfo?.jobName || String(selectedJob?.name || ""),
        },
      }));
      setTargetJobId("");
      showFlash("Photos linked to selected job", "success");
    } catch (error) {
      const msg = error?.message || "Could not link photos to selected job.";
      setState((prev) => ({ ...prev, processingError: msg, photoSyncError: msg }));
      showFlash(msg, "error");
    } finally {
      setLinkingJob(false);
    }
  };

  const handleRetryPackCheck = async () => {
    if (!state.jobId || packCheckRetrying) return;
    try {
      setPackCheckRetrying(true);
      setState((prev) => ({
        ...prev,
        processingError: "",
        processingPhase: "Packaging",
        processingMessage: "Retrying estimator pack check",
      }));
      const packStatus = await pollEstimatorPackReady(state.jobId, {
        platform: state.platform || "xactimate",
        include_all: true,
        include_rationale: true,
        optimize_entry_order: 1,
        maxAttempts: 20,
        intervalMs: 1000,
      });
      if (packStatus.ready) {
        setState((prev) => ({
          ...prev,
          packReady: true,
          packRef: packStatus.ref || { kind: "job", id: state.jobId },
          processingPhase: "Ready",
          processingProgress: 100,
          processingMessage: "Estimator pack is ready",
        }));
        showFlash("Estimator pack is ready", "success");
        return;
      }
      setState((prev) => ({
        ...prev,
        processingError: packStatus.error || "Estimator pack is not ready yet. Try again shortly.",
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        processingError: error?.message || "Estimator pack check failed",
      }));
    } finally {
      setPackCheckRetrying(false);
    }
  };

  const handleJobInfoChange = (field, value) => {
    setState((prev) => ({
      ...prev,
      jobInfo: {
        ...(prev.jobInfo || {}),
        [field]: value,
      },
    }));
  };

  const handleJobInfoBlur = (field) => {
    if (field === "notes") return;
    setState((prev) => ({
      ...prev,
      jobInfo: {
        ...(prev.jobInfo || {}),
        [field]: standardizeCapitalization(prev.jobInfo?.[field] || ""),
      },
    }));
  };

  const normalizedJobInfo = (jobInfo) => ({
    ...(jobInfo || {}),
    jobName: standardizeCapitalization(jobInfo?.jobName || ""),
    insuredName: standardizeCapitalization(jobInfo?.insuredName || ""),
    claimNumber: String(jobInfo?.claimNumber || "").trim(),
    propertyAddress: standardizeCapitalization(jobInfo?.propertyAddress || ""),
    notes: String(jobInfo?.notes || ""),
  });

  const hasSavableJobInfo = (jobInfo) => {
    const clean = normalizedJobInfo(jobInfo || {});
    return Boolean(
      clean.jobName ||
        clean.insuredName ||
        clean.claimNumber ||
        clean.propertyAddress ||
        String(clean.notes || "").trim()
    );
  };

  const handleSaveJobInfo = async () => {
    try {
      const cleanJobInfo = normalizedJobInfo(state.jobInfo || {});
      if (!hasSavableJobInfo(cleanJobInfo)) {
        showFlash("Enter job information before saving this build to Jobs.", "error");
        return;
      }
      const payload = {
        jobId: state.jobId || "",
        setId: state.setId || "",
        updatedAt: new Date().toISOString(),
        jobInfo: cleanJobInfo,
      };
      if (payload.jobId) {
        localStorage.setItem(`build_job_info:${payload.jobId}`, JSON.stringify(payload));
        await updateJob(payload.jobId, {
          name: cleanJobInfo.jobName || cleanJobInfo.insuredName || cleanJobInfo.propertyAddress || "Untitled Job",
          notes: cleanJobInfo.notes,
          intake_source: cleanJobInfo.notes,
          intake_json: {
            jobName: cleanJobInfo.jobName,
            insuredName: cleanJobInfo.insuredName,
            claimNumber: cleanJobInfo.claimNumber,
            propertyAddress: cleanJobInfo.propertyAddress,
          },
          is_hidden: false,
        });
      }
      setState((prev) => ({
        ...prev,
        jobInfo: {
          ...cleanJobInfo,
          savedAt: payload.updatedAt,
        },
      }));
      showFlash("Job information saved", "success");
    } catch (_error) {
      showFlash("Could not save job information", "error");
    }
  };

  const handleClearLabelIntake = () => {
    setLabelIntakeMessage("");
    setState((prev) => ({
      ...prev,
      jobInfo: {
        ...(prev.jobInfo || {}),
        notes: "",
      },
    }));
  };

  const handleRunLabelSmartIntake = async () => {
    const text = String(state.jobInfo?.notes || "").trim();
    if (!text) {
      setLabelIntakeMessage("Paste intake text first.");
      return;
    }
    setLabelIntakeBusy(true);
    setLabelIntakeMessage("");
    try {
      const result = await extractCrmIntake({ text });
      const job = result?.job && typeof result.job === "object" ? result.job : {};
      const contact = result?.contact && typeof result.contact === "object" ? result.contact : {};
      const candidateName = firstNonEmptyString(
        contact.name,
        job.property_owner,
        job.homeowner,
        job.customer,
        job.insured,
        job.name
      );
      const candidateAddress = firstNonEmptyString(
        job.property_address,
        job.address,
        contact.property_address,
        contact.address
      );
      const candidateClaim = firstNonEmptyString(job.claim_number, job.claimNumber, result?.claim_number);
      setState((prev) => ({
        ...prev,
        jobInfo: {
          ...(prev.jobInfo || {}),
          jobName: candidateName
            ? standardizeCapitalization(candidateName)
            : standardizeCapitalization(prev.jobInfo?.jobName || ""),
          insuredName: candidateName
            ? standardizeCapitalization(candidateName)
            : standardizeCapitalization(prev.jobInfo?.insuredName || ""),
          claimNumber: candidateClaim || prev.jobInfo?.claimNumber || "",
          propertyAddress: candidateAddress
            ? standardizeCapitalization(candidateAddress)
            : standardizeCapitalization(prev.jobInfo?.propertyAddress || ""),
          notes: prev.jobInfo?.notes || text,
          savedAt: "",
        },
      }));
      setLabelIntakeMessage(candidateName || candidateAddress ? "Smart intake filled job information." : "No job fields were found.");
    } catch (error) {
      setLabelIntakeMessage(error?.message || "Smart intake failed.");
    } finally {
      setLabelIntakeBusy(false);
    }
  };

  const movePhotoInState = (fromId, toId) => {
    const sourceId = String(fromId || "");
    const targetId = String(toId || "");
    if (!sourceId || !targetId || sourceId === targetId) return null;
    const rows = Array.isArray(summaryPhotoRows) ? summaryPhotoRows : [];
    const fromIndex = rows.findIndex((p) => String(p?.id || "") === sourceId);
    const toIndex = rows.findIndex((p) => String(p?.id || "") === targetId);
    if (fromIndex < 0 || toIndex < 0) return null;
    const nextRows = [...rows];
    const [moved] = nextRows.splice(fromIndex, 1);
    nextRows.splice(toIndex, 0, moved);
    const nextIds = nextRows.map((p) => String(p?.id || "")).filter(Boolean);
    const orderedMap = new Map(nextRows.map((p) => [String(p?.id || ""), p]));
    setState((prev) => ({
      ...prev,
      photoIds: nextIds,
      jobPhotos: nextIds.map((id) => orderedMap.get(id) || prev.jobPhotos.find((p) => String(p?.id || "") === id)).filter(Boolean),
      setId: "",
      packRef: null,
      packReady: false,
      buildRunRequested: false,
    }));
    return nextIds;
  };

  const savePhotoOrder = async (nextIds) => {
    if (!Array.isArray(nextIds) || nextIds.length < 1 || !state.jobId) return;
    try {
      const result = await reorderJobPhotos(state.jobId, nextIds);
      const photos = Array.isArray(result?.photos) ? result.photos : [];
      if (photos.length > 0) {
        setState((prev) => ({
          ...prev,
          photoIds: photos.map((p) => p.id).filter(Boolean),
          jobPhotos: mergePhotosWithPreviews(
            photos.map((p) => p.id).filter(Boolean),
            photos,
            prev.photos
          ),
        }));
      }
      showFlash("Photo order saved", "success");
    } catch (error) {
      setState((prev) => ({ ...prev, processingError: error?.message || "Photo reorder failed" }));
      showFlash("Photo reorder failed", "error");
      await refreshJobPhotos(state.jobId, { silent: true });
    }
  };

  const handlePhotoDrop = async (targetId) => {
    const sourceId = dragPhotoId;
    setDragPhotoId("");
    setDragOverPhotoId("");
    const nextIds = movePhotoInState(sourceId, targetId);
    await savePhotoOrder(nextIds);
  };

  const handlePhotoMoveByOffset = async (photoId, offset) => {
    const rows = Array.isArray(summaryPhotoRows) ? summaryPhotoRows : [];
    const fromIndex = rows.findIndex((p) => String(p?.id || "") === String(photoId || ""));
    if (fromIndex < 0) return;
    const toIndex = fromIndex + Number(offset || 0);
    if (toIndex < 0 || toIndex >= rows.length) return;
    const nextRows = [...rows];
    const [moved] = nextRows.splice(fromIndex, 1);
    nextRows.splice(toIndex, 0, moved);
    const nextIds = nextRows.map((p) => String(p?.id || "")).filter(Boolean);
    const orderedMap = new Map(nextRows.map((p) => [String(p?.id || ""), p]));
    setState((prev) => ({
      ...prev,
      photoIds: nextIds,
      jobPhotos: nextIds.map((id) => orderedMap.get(id) || prev.jobPhotos.find((p) => String(p?.id || "") === id)).filter(Boolean),
      setId: "",
      packRef: null,
      packReady: false,
      buildRunRequested: false,
    }));
    await savePhotoOrder(nextIds);
  };

  const summaryPhotoRows = React.useMemo(() => {
    if (!Array.isArray(state.jobPhotos) || state.jobPhotos.length < 1) return [];
    const ordered = mergePhotosWithPreviews(state.photoIds, state.jobPhotos, state.photos);
    return ordered.map((photo, index) => {
      const labels = getPhotoLabelFields(photo);
      const room = String(labels.room || "").trim();
      const description = String(labels.description || "").trim();
      const displayRoom = formatRoomName(room || "(room not set)");
      const displayDescription = description || "(description not set)";
      return {
        ...photo,
        displayRoom,
        displayDescription,
        label: `Room: ${displayRoom} | Damage: ${displayDescription}`,
        thumbnail_url: photo.thumbnail_url || "",
        confidence: labels.confidence,
      };
    });
  }, [state.jobPhotos, state.photoIds, state.photos]);

  const labelCompleteness = React.useMemo(() => {
    const rows = Array.isArray(summaryPhotoRows) ? summaryPhotoRows : [];
    const total = rows.length;
    const complete = rows.filter((photo) => {
      const room = String(photo?.room || "").trim();
      const description = String(photo?.description || "").trim();
      return room && description;
    }).length;
    const percent = total > 0 ? Math.round((complete / total) * 100) : 0;
    return { total, complete, remaining: Math.max(0, total - complete), percent };
  }, [summaryPhotoRows]);

  const workflowStatusRows = React.useMemo(() => {
    const selectedPhotoCount = Array.isArray(state.photos) ? state.photos.length : 0;
    const uploadedPhotoCount = Array.isArray(state.photoIds) ? state.photoIds.length : 0;
    const photosComplete = selectedPhotoCount > 0 && uploadedPhotoCount >= selectedPhotoCount && !state.uploadingPhotos;
    const activePhotoProgress = Math.max(1, Math.min(99, Number(state.uploadProgress) || 1));
    const answersComplete =
      state.wantsPhotoLabels !== null &&
      state.wantsLineItemSuggestions !== null &&
      LOSS_TYPE_OPTIONS.includes(String(state.lossType || "").toLowerCase()) &&
      state.wantsCodeUpgrades !== null &&
      (!state.wantsCodeUpgrades || /^\d{5}(-\d{4})?$/.test(String(state.propertyZip || "").trim()));
    const guidelineComplete = Boolean(state.guidelineSkipped || state.guidelineDocumentId || state.selectedGuidelineId);
    const sketchComplete = Boolean(state.sketchSkipped || state.sketchDocumentId);
    const scopeComplete = state.processingDone || state.scopeProgress >= 100;
    const packageComplete = state.processingDone || state.packageProgress >= 100;
    const lineItemsSkipped = state.wantsLineItemSuggestions === false;
    return [
      {
        label: "Photos + Labels",
        value: state.uploadingPhotos ? activePhotoProgress : photosComplete ? 100 : uploadedPhotoCount > 0 ? 90 : 0,
        detail: state.uploadingPhotos
          ? uploadedPhotoCount >= selectedPhotoCount && selectedPhotoCount > 0
            ? "Finalizing photo labels"
            : state.uploadStatusText || `${Math.min(uploadedPhotoCount, selectedPhotoCount)} of ${selectedPhotoCount} photos uploaded`
          : state.uploadStatusText ||
            (selectedPhotoCount > 0
              ? `${Math.min(uploadedPhotoCount, selectedPhotoCount)} of ${selectedPhotoCount} photos uploaded and labeled`
              : "No photos selected"),
        tone: state.uploadingPhotos ? "info" : photosComplete ? "success" : uploadedPhotoCount > 0 ? "info" : "neutral",
      },
      {
        label: "Questions",
        value: answersComplete ? 100 : state.currentStepId === STEP_IDS.A1 ? 50 : 0,
        detail: answersComplete ? "Required answers complete" : "Waiting for required answers",
        tone: answersComplete ? "success" : state.currentStepId === STEP_IDS.A1 ? "info" : "neutral",
      },
      {
        label: "Guidelines",
        value: guidelineComplete ? 100 : clampPercent(state.guidelineProgress),
        detail: state.guidelineSkipped
          ? "Skipped"
          : state.guidelineDocumentId || state.selectedGuidelineId
            ? "Ready"
            : state.guideline
              ? "Waiting to upload"
              : "Optional",
        tone: guidelineComplete ? "success" : state.guidelineProgress > 0 ? "info" : "neutral",
      },
      {
        label: "Sketch",
        value: sketchComplete ? 100 : clampPercent(state.sketchProgress),
        detail: state.sketchSkipped ? "Skipped" : state.sketchDocumentId ? "Ready" : state.sketch ? "Waiting to upload" : "Optional",
        tone: sketchComplete ? "success" : state.sketchProgress > 0 ? "info" : "neutral",
      },
      {
        label: "Scope Suggestions",
        value: state.wantsLineItemSuggestions === false ? 100 : scopeComplete ? 100 : clampPercent(state.scopeProgress),
        detail:
          state.wantsLineItemSuggestions === false
            ? "Skipped by user"
            : canUseLineItemSuggestions === false
            ? "Not included on this plan"
            : scopeComplete
              ? "Complete"
              : state.scopeProgress > 0
                ? "Generating"
                : "Not started",
        tone: state.wantsLineItemSuggestions === false || scopeComplete ? "success" : state.scopeProgress > 0 ? "info" : "neutral",
      },
      {
        label: "Estimator Pack",
        value: lineItemsSkipped ? 100 : packageComplete ? 100 : clampPercent(state.packageProgress),
        detail: lineItemsSkipped ? "Skipped by user" : packageComplete ? "Complete" : state.packageProgress > 0 ? "Packaging" : "Not started",
        tone: lineItemsSkipped || packageComplete ? "success" : state.packageProgress > 0 ? "info" : "neutral",
      },
    ];
  }, [
    state.currentStepId,
    state.guideline,
    state.guidelineDocumentId,
    state.guidelineProgress,
    state.guidelineSkipped,
    state.packageProgress,
    state.photoIds,
    state.photos,
    state.propertyZip,
    state.scopeProgress,
    state.selectedGuidelineId,
    state.sketch,
    state.sketchDocumentId,
    state.sketchProgress,
    state.sketchSkipped,
    state.uploadProgress,
    state.uploadStatusText,
    state.uploadingPhotos,
    state.wantsCodeUpgrades,
    state.wantsLineItemSuggestions,
    state.wantsPhotoLabels,
    state.lossType,
    state.processingDone,
    canUseLineItemSuggestions,
  ]);

  const hasUnsavedPhotoDrafts = React.useMemo(() => Object.keys(photoEditDrafts).length > 0, [photoEditDrafts]);
  const groupedLineItemsByRoom = React.useMemo(
    () => groupLineItemsByRoomAndArea(state.lineItems),
    [state.lineItems]
  );
  const roomLabelMemory = React.useMemo(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("photo_label_memory:room") || "[]");
      return Array.isArray(parsed) ? parsed.map((x) => String(x || "").trim()).filter(Boolean) : [];
    } catch (_e) {
      return [];
    }
  }, [state.jobPhotos]);
  const descriptionLabelMemory = React.useMemo(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("photo_label_memory:description") || "[]");
      return Array.isArray(parsed) ? parsed.map((x) => String(x || "").trim()).filter(Boolean) : [];
    } catch (_e) {
      return [];
    }
  }, [state.jobPhotos]);
  const renderPhotoUploadStatus = () =>
    state.uploadingPhotos ? (() => {
      const progress = Math.max(1, Math.min(99, Number(state.uploadProgress) || 1));
      const selectedPhotoCount = Array.isArray(state.photos) ? state.photos.length : 0;
      const uploadedPhotoCount = Array.isArray(state.photoIds) ? state.photoIds.length : 0;
      const finalizing = progress >= 99 || (selectedPhotoCount > 0 && uploadedPhotoCount >= selectedPhotoCount);
      return (
        <div className="wizard-labels-section">
          <div className="wizard-progress-wrap">
            <Progress value={progress} />
            <span className="wizard-muted">{Math.round(progress)}%</span>
            <Badge tone="info">{finalizing ? "Finalizing Labels" : "Uploading Photos"}</Badge>
          </div>
          <p className="wizard-muted">
            {finalizing
              ? "Photos have uploaded. ES is finishing the photo labels."
              : "Photo upload is continuing in the background. You can keep answering questions."}
          </p>
        </div>
      );
    })() : null;
  const renderWorkflowStatusBars = () => (
    <div className="wizard-status-stack">
      {workflowStatusRows.map((row) => {
        const value = clampPercent(row.value);
        return (
          <div className="wizard-status-row" key={row.label}>
            <div className="wizard-status-head">
              <strong>{row.label}</strong>
              <span className="wizard-muted">{Math.round(value)}%</span>
            </div>
            <Progress value={value} />
            <div className="wizard-status-detail">
              <span className="wizard-muted">{row.detail}</span>
              <Badge tone={row.tone === "success" ? "success" : "info"}>
                {row.tone === "success" ? "Complete" : row.tone === "info" ? "In Progress" : "Pending"}
              </Badge>
            </div>
          </div>
        );
      })}
    </div>
  );
  const renderA0 = () => (
    <Card className="wizard-card wizard-card-centered">
      <CardHeader>
        <h1 className="wizard-title">Photos Upload</h1>
        <p className="wizard-subtitle">Upload photos to start building an estimate.</p>
      </CardHeader>
      <CardBody>
      <input
        ref={photosInputRef}
        id="wizard-photos-upload"
        type="file"
        multiple
        accept="image/*"
        className="wizard-hidden-input"
        onChange={handlePhotosChange}
      />
      <label className="wizard-upload-button" htmlFor="wizard-photos-upload">
        Upload Photos
      </label>
      {state.photos.length > 0 ? (
        <div
          ref={selectedPhotoGridRef}
          className="wizard-upload-photo-grid"
          onDragOver={handleSelectedPhotoGridDragOver}
        >
          {state.photos.map((f, idx) => (
            <div
              key={`${f.name}-${f.size}-${idx}`}
              className={`wizard-upload-photo-card${selectedPhotoDragIndex === idx ? " dragging" : ""}`}
              title={f.name || `Photo ${idx + 1}`}
              draggable
              onDragStart={(e) => {
                setSelectedPhotoDragIndex(idx);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(idx));
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleSelectedPhotoDrop(idx);
              }}
              onDragEnd={() => {
                setSelectedPhotoDragIndex(null);
                stopSelectedPhotoDragScroll();
              }}
            >
              <div className="wizard-upload-photo-reorder">
                <span className="wizard-upload-photo-handle" title="Drag to reorder" aria-label="Drag to reorder">
                  Drag
                </span>
                <div className="wizard-upload-photo-buttons">
                  <button
                    type="button"
                    className="wizard-upload-photo-button"
                    onClick={() => handleSelectedPhotoMove(idx, idx - 1)}
                    disabled={idx === 0}
                    aria-label={`Move ${f.name || `photo ${idx + 1}`} up`}
                    title="Move up"
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    className="wizard-upload-photo-button"
                    onClick={() => handleSelectedPhotoMove(idx, 0)}
                    disabled={idx === 0}
                    aria-label={`Move ${f.name || `photo ${idx + 1}`} to top`}
                    title="Move to top"
                  >
                    Top
                  </button>
                  <button
                    type="button"
                    className="wizard-upload-photo-button"
                    onClick={() => handleSelectedPhotoMove(idx, idx + 1)}
                    disabled={idx === state.photos.length - 1}
                    aria-label={`Move ${f.name || `photo ${idx + 1}`} down`}
                    title="Move down"
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    className="wizard-upload-photo-button"
                    onClick={() => handleSelectedPhotoMove(idx, state.photos.length - 1)}
                    disabled={idx === state.photos.length - 1}
                    aria-label={`Move ${f.name || `photo ${idx + 1}`} to bottom`}
                    title="Move to bottom"
                  >
                    Bottom
                  </button>
                  <button
                    type="button"
                    className="wizard-upload-photo-button"
                    onClick={() => handleSelectedPhotoDelete(idx)}
                    disabled={state.uploadingPhotos}
                    aria-label={`Delete ${f.name || `photo ${idx + 1}`}`}
                    title="Delete photo"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {f.thumbnail_url ? (
                <img
                  src={f.thumbnail_url}
                  alt={f.name || `Photo ${idx + 1}`}
                  className="wizard-upload-photo-img"
                />
              ) : (
                <div className="wizard-upload-photo-empty">
                  No preview
                </div>
              )}
              <div className="wizard-upload-photo-name">{f.name || `Photo ${idx + 1}`}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="wizard-muted">No photos selected yet.</p>
      )}
      {state.uploadingPhotos ? (
        <div className="wizard-uploading-indicator">
          <Spinner size="sm" />
          <span className="wizard-muted">
            {Number(state.uploadProgress) >= 99 ? "Finalizing labels..." : "Uploading..."}
          </span>
        </div>
      ) : null}
      <div className="wizard-actions">
        <Button onClick={handleA0Continue} disabled={state.photos.length < 1}>
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA1 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Options</h1>
        <p className="wizard-subtitle">Choose optional enhancements before processing.</p>
      </CardHeader>
      <CardBody>
      {state.photoSyncError ? <p className="wizard-warning">{state.photoSyncError}</p> : null}
      {renderPhotoUploadStatus()}
      <div className="wizard-options-block">
        <p className="wizard-subtitle">Do you want to label photo(s)?</p>
        <div className="wizard-actions">
          <Button
            variant={state.wantsPhotoLabels === false ? "primary" : "secondary"}
            onClick={() => setState((prev) => ({ ...prev, wantsPhotoLabels: false }))}
          >
            No
          </Button>
          <Button
            variant={state.wantsPhotoLabels === true ? "primary" : "secondary"}
            onClick={() => setState((prev) => ({ ...prev, wantsPhotoLabels: true }))}
          >
            Yes
          </Button>
        </div>
      </div>
      <div className="wizard-options-block">
        <p className="wizard-subtitle">Do you want line item suggestions?</p>
        <div className="wizard-actions">
          <Button
            variant={state.wantsLineItemSuggestions === false ? "primary" : "secondary"}
            onClick={() =>
              setState((prev) => ({
                ...prev,
                wantsLineItemSuggestions: false,
                platform: "",
                guideline: null,
                guidelineDocumentId: "",
                guidelineSkipped: true,
                selectedGuidelineId: "",
                sketch: null,
                sketchDocumentId: "",
                sketchSkipped: true,
                processingError: "",
              }))
            }
          >
            No
          </Button>
          <Button
            variant={state.wantsLineItemSuggestions === true ? "primary" : "secondary"}
            disabled={canUseLineItemSuggestions === false}
            onClick={() =>
              setState((prev) => ({
                ...prev,
                wantsLineItemSuggestions: true,
                processingError: "",
              }))
            }
          >
            Yes
          </Button>
          {canUseLineItemSuggestions === false ? (
            <Badge tone="warning">Paid plan required</Badge>
          ) : null}
        </div>
      </div>
      <div className="wizard-options-block">
        <p className="wizard-subtitle">Do you want property code upgrades applied?</p>
        <div className="wizard-actions">
          <Button
            variant={state.wantsCodeUpgrades === false ? "primary" : "secondary"}
            onClick={() => setState((prev) => ({ ...prev, wantsCodeUpgrades: false, propertyZip: "" }))}
          >
            No
          </Button>
          <Button
            variant={state.wantsCodeUpgrades === true ? "primary" : "secondary"}
            onClick={() => setState((prev) => ({ ...prev, wantsCodeUpgrades: true }))}
          >
            Yes
          </Button>
        </div>
        {state.wantsCodeUpgrades ? (
          <div style={{ marginTop: 10 }}>
            <Input
              className="wizard-input"
              value={state.propertyZip}
              placeholder="Property ZIP code (e.g., 75001)"
              onChange={(e) => setState((prev) => ({ ...prev, propertyZip: e.target.value }))}
            />
            <p className="wizard-muted" style={{ marginTop: 6 }}>
              The app will use this ZIP to look up current code signals and apply code-compliance upgrade suggestions.
            </p>
          </div>
        ) : null}
      </div>
      <div className="wizard-options-block">
        <p className="wizard-subtitle">Type of loss</p>
        <div className="wizard-actions">
          {LOSS_TYPE_OPTIONS.map((loss) => (
            <Button
              key={loss}
              variant={state.lossType === loss ? "primary" : "secondary"}
              onClick={() => setState((prev) => ({ ...prev, lossType: loss, processingError: "" }))}
            >
              {toTitleCase(loss)}
            </Button>
          ))}
        </div>
      </div>
      <AiDisclaimer
        title="AI + Public Data Disclaimer"
        detail="Loss classification, code upgrades, and public-safety contacts use AI and third-party data. Verify jurisdiction, code authority, and contact details before acting."
      />
      <div className="wizard-actions">
        <Button
          onClick={handleA1Continue}
          disabled={
            state.wantsPhotoLabels === null ||
            state.wantsLineItemSuggestions === null ||
            !LOSS_TYPE_OPTIONS.includes(String(state.lossType || "").toLowerCase()) ||
            state.wantsCodeUpgrades === null ||
            (state.wantsCodeUpgrades && !/^\d{5}(-\d{4})?$/.test(String(state.propertyZip || "").trim()))
          }
        >
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA2 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Estimating Guidelines Upload (optional)</h1>
        <p className="wizard-subtitle">Select a saved guideline or upload a new one.</p>
        <div style={{ marginTop: 8 }}>
          <Link to="/app/guidelines">
            <Button variant="ghost" size="sm">Open Guidelines Library</Button>
          </Link>
        </div>
      </CardHeader>
      <CardBody>
      {renderPhotoUploadStatus()}
      {state.availableGuidelines.length > 0 ? (
        <div className="wizard-actions" style={{ marginTop: 0 }}>
          <select
            className="wizard-input"
            value={state.selectedGuidelineId}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                selectedGuidelineId: e.target.value,
                guidelineDocumentId: e.target.value || prev.guidelineDocumentId,
                guidelineSkipped: !e.target.value,
              }))
            }
          >
            <option value="">Choose from saved guidelines</option>
            {state.availableGuidelines.map((g) => (
              <option key={g.id} value={g.id}>
                {g.filename}
              </option>
            ))}
          </select>
          <Button variant="secondary" onClick={handleDeleteSelectedGuideline} disabled={!state.selectedGuidelineId}>
            Delete Saved Guideline
          </Button>
        </div>
      ) : null}
      <p className="wizard-muted">Or upload a guidelines file (PDF, DOC, DOCX, or TXT).</p>
      <input
        ref={guidelineInputRef}
        id="wizard-guidelines-upload"
        type="file"
        accept=".pdf,.doc,.docx,.txt,text/plain,application/pdf"
        className="wizard-hidden-input"
        onChange={handleGuidelineChange}
      />
      <label className="wizard-upload-button small" htmlFor="wizard-guidelines-upload">
        Upload Guidelines
      </label>
      {state.guideline ? (
        <div className="wizard-file-pill">
          <span>{state.guideline.name}</span>
        </div>
      ) : (
        <p className="wizard-muted">No guidelines uploaded.</p>
      )}
      <div className="wizard-actions">
        <Button variant="secondary" onClick={skipGuideline}>
          Skip
        </Button>
        <Button onClick={handleA2Continue}>
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA3 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Platform Select</h1>
      </CardHeader>
      <CardBody>
      {renderPhotoUploadStatus()}
      <div className="wizard-platform-grid">
        <RadioCard
          name="platform"
          value="xactimate"
          title="Xactimate"
          description="Use Xactimate-oriented estimate structure."
          checked={state.platform === "xactimate"}
          onChange={() => setState((prev) => ({ ...prev, platform: "xactimate" }))}
        />
        <RadioCard
          name="platform"
          value="symbility"
          title="Symbility"
          description="Use Symbility-oriented estimate structure."
          checked={state.platform === "symbility"}
          onChange={() => setState((prev) => ({ ...prev, platform: "symbility" }))}
        />
      </div>
      <div className="wizard-actions">
        <Button onClick={handleA3Continue} disabled={!state.platform}>
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA4 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Sketch Upload (optional)</h1>
      </CardHeader>
      <CardBody>
      {renderPhotoUploadStatus()}
      <p className="wizard-warning">Measurements may not be accurate without a sketch.</p>
      <input
        ref={sketchInputRef}
        id="wizard-sketch-upload"
        type="file"
        accept=".pdf,image/*"
        className="wizard-hidden-input"
        onChange={handleSketchChange}
      />
      <label className="wizard-upload-button small" htmlFor="wizard-sketch-upload">
        Upload Sketch
      </label>
      {state.sketch ? (
        <div className="wizard-file-pill">
          <span>{state.sketch.name}</span>
        </div>
      ) : (
        <p className="wizard-muted">No sketch uploaded.</p>
      )}
      <div className="wizard-actions">
        <Button variant="secondary" onClick={skipSketch}>
          Skip
        </Button>
        <Button onClick={handleA4Continue}>
          Continue
        </Button>
      </div>
      </CardBody>
    </Card>
  );

  const renderA5 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Status / Processing</h1>
      </CardHeader>
      <CardBody>
      {renderPhotoUploadStatus()}
      {renderWorkflowStatusBars()}
      <ul className="wizard-processing-list">
        {PROCESS_PHASES.map((phase) => {
          const current = phaseIndex(state.processingPhase);
          const idx = phaseIndex(phase);
          const isDone = idx < current || (state.processingDone && phase === "Ready");
          const isActive = idx === current && !isDone;
          return (
            <li key={phase} className={`wizard-processing-item${isDone ? " done" : ""}`}>
              <span className={`wizard-processing-icon${isActive ? " active" : ""}`}>
                {isDone ? "OK" : <Spinner size="sm" />}
              </span>
              <span>{phase}</span>
            </li>
          );
        })}
      </ul>
      <p className="wizard-muted">{state.processingMessage || "Processing..."}</p>
      {state.processingError ? <p className="wizard-warning">{state.processingError}</p> : null}
      <div className="wizard-actions">
        {state.processingDone ? (
          <Button onClick={() => setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }))}>
            View Results
          </Button>
        ) : null}
        {state.processingError ? (
          <Button onClick={retryBuild}>
            Retry
          </Button>
        ) : null}
      </div>
      </CardBody>
    </Card>
  );

  const renderA6 = () => (
    <Card className="wizard-card">
      <CardHeader className="flex items-center justify-between gap-3">
        <h1 className="wizard-title">Label Review</h1>
        {state.packReady ? <Badge tone="success">Ready</Badge> : null}
      </CardHeader>
      <CardBody>
      <div className="wizard-summary-grid">
        <div>
          <strong>Job</strong>
          <p>{state.jobId || "Not created"}</p>
        </div>
        <div>
          <strong>Photos</strong>
          <p>
            {state.photos.length} selected ({state.photoIds.length} uploaded)
          </p>
        </div>
        <div>
          <strong>Photo Labels</strong>
          <p>
            {labelCompleteness.complete}/{labelCompleteness.total} complete
          </p>
        </div>
        <div>
          <strong>Line Item Suggestions</strong>
          <p>{state.wantsLineItemSuggestions ? "Enabled" : "Skipped"}</p>
        </div>
        {state.wantsLineItemSuggestions ? (
          <>
            <div>
              <strong>Guidelines</strong>
              <p>{state.guidelineDocumentId ? "Uploaded" : state.guidelineSkipped ? "Skipped" : "Not provided"}</p>
            </div>
            <div>
              <strong>Sketch</strong>
              <p>{state.sketchDocumentId ? "Uploaded" : state.sketchSkipped ? "Skipped" : "Not provided"}</p>
            </div>
            <div>
              <strong>Platform</strong>
              <p>{state.platform || "Not selected"}</p>
            </div>
          </>
        ) : null}
        <div>
          <strong>Code Upgrades</strong>
          <p>
            {state.wantsCodeUpgrades
              ? `Enabled${state.propertyZip ? ` (ZIP ${state.propertyZip})` : ""}`
              : "Disabled"}
          </p>
        </div>
        <div>
          <strong>Loss Type</strong>
          <p>{state.lossType ? toTitleCase(state.lossType) : "Not selected"}</p>
        </div>
      </div>

      {summaryPhotoRows.length > 0 ? (
        <div className="wizard-labels-section">
          <h3 className="wizard-subtitle">Link Photos To Job</h3>
          <div className="wizard-photo-meta-grid">
            <select
              className="wizard-input"
              value={targetJobId}
              onChange={(e) => setTargetJobId(e.target.value)}
            >
              <option value="">Select existing job</option>
              {availableJobs.map((job) => {
                const id = String(job?.id || "");
                return (
                  <option key={`link-job-${id}`} value={id} disabled={id === state.jobId}>
                    {String(job?.name || "").trim() || id}
                  </option>
                );
              })}
            </select>
            <Button
              variant="secondary"
              onClick={handleLinkPhotosToExistingJob}
              disabled={!targetJobId || targetJobId === state.jobId || linkingJob}
            >
              {linkingJob ? "Linking..." : "Link Labeled Photos"}
            </Button>
          </div>
        </div>
      ) : null}

      {!hasSavableJobInfo(state.jobInfo || {}) ? (
        <p className="wizard-warning">
          Job information has not been entered. This build will not appear on the Jobs page unless job information is saved,
          but downloads will remain available from this review page.
        </p>
      ) : null}

      <div className="wizard-labels-section">
        <h3 className="wizard-subtitle">Smart Intake Prompt</h3>
        <textarea
          className="wizard-input"
          style={{ minHeight: 90, width: "100%" }}
          value={state.jobInfo?.notes || ""}
          placeholder="Paste or edit smart intake/job notes for this estimate."
          onChange={(e) => handleJobInfoChange("notes", e.target.value)}
        />
        <div className="wizard-actions">
          <Button onClick={handleRunLabelSmartIntake} disabled={labelIntakeBusy || !String(state.jobInfo?.notes || "").trim()}>
            {labelIntakeBusy ? "Extracting..." : "Extract Job Info"}
          </Button>
          <Button variant="secondary" onClick={handleClearLabelIntake}>
            Clear Prompt
          </Button>
          {labelIntakeMessage ? (
            <span className={labelIntakeMessage.toLowerCase().includes("failed") ? "wizard-warning" : "wizard-muted"}>
              {labelIntakeMessage}
            </span>
          ) : null}
        </div>
      </div>

      <div className="wizard-labels-section">
        <h3 className="wizard-subtitle">Job Information</h3>
        <div className="wizard-photo-meta-grid">
          <Input
            value={state.jobInfo?.jobName || ""}
            placeholder="Job name"
            onChange={(e) => handleJobInfoChange("jobName", e.target.value)}
            onBlur={() => handleJobInfoBlur("jobName")}
          />
          <Input
            value={state.jobInfo?.insuredName || ""}
            placeholder="Insured / customer name"
            onChange={(e) => handleJobInfoChange("insuredName", e.target.value)}
            onBlur={() => handleJobInfoBlur("insuredName")}
          />
          <Input
            value={state.jobInfo?.claimNumber || ""}
            placeholder="Claim number"
            onChange={(e) => handleJobInfoChange("claimNumber", e.target.value)}
            onBlur={() => handleJobInfoBlur("claimNumber")}
          />
          <Input
            className="sm:col-span-3"
            value={state.jobInfo?.propertyAddress || ""}
            placeholder="Property address"
            onChange={(e) => handleJobInfoChange("propertyAddress", e.target.value)}
            onBlur={() => handleJobInfoBlur("propertyAddress")}
          />
          <Input
            className="sm:col-span-3"
            value={state.jobInfo?.notes || ""}
            placeholder="Notes"
            onChange={(e) => handleJobInfoChange("notes", e.target.value)}
          />
        </div>
        <div className="wizard-actions">
          <Button variant="secondary" onClick={handleSaveJobInfo}>Save Job Information</Button>
          {state.jobInfo?.savedAt ? (
            <Badge tone="success">Saved {new Date(state.jobInfo.savedAt).toLocaleString()}</Badge>
          ) : null}
        </div>
      </div>
      <AiDisclaimer
        title="Photo Labeling Disclaimer"
        detail="Room, description, and confidence are AI-assisted estimates. Confirm and edit labels before finalizing."
      />

      {CIVIC_CONTACT_LOSS_TYPES.has(String(state.lossType || "").toLowerCase()) ? (
        <div className="wizard-labels-section">
          <h3 className="wizard-subtitle">Emergency Contacts</h3>
          {publicSafetyLoading ? (
            <p className="wizard-muted inline-flex items-center gap-2"><Spinner size="sm" />Loading contacts...</p>
          ) : publicSafetyContacts.length > 0 ? (
            <ul className="wizard-label-list">
              {publicSafetyContacts.map((contact, idx) => (
                <li key={`${contact.type || "agency"}-${contact.name || "contact"}-${idx}`} className="wizard-label-row">
                  <div className="wizard-label-main" style={{ display: "block" }}>
                    <div className="wizard-photo-name">{contact.name || toTitleCase(contact.type || "Agency")}</div>
                    <p className="wizard-muted">
                      {contact.phone ? `Phone: ${contact.phone}` : "Phone: not listed"}
                    </p>
                    <p className="wizard-muted">
                      {contact.address ? `Address: ${contact.address}` : "Address: not listed"}
                    </p>
                    {Number.isFinite(Number(contact.distance_miles)) ? (
                      <p className="wizard-muted">Distance: {Number(contact.distance_miles).toFixed(2)} mi</p>
                    ) : null}
                    <div className="wizard-actions">
                      {contact.website ? (
                        <a href={contact.website} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="secondary">Open Website</Button>
                        </a>
                      ) : null}
                      {contact.source ? <Badge tone="info">{contact.source}</Badge> : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : publicSafetyError ? (
            <p className="wizard-warning">{publicSafetyError}</p>
          ) : (
            <p className="wizard-muted">
              Add a property ZIP and/or address above to look up fire or police contact details.
            </p>
          )}
          {publicSafetyResolvedLocation ? (
            <p className="wizard-muted" style={{ marginTop: 8 }}>Resolved location: {publicSafetyResolvedLocation}</p>
          ) : null}
        </div>
      ) : null}

      {state.wantsPhotoLabels && summaryPhotoRows.length > 0 ? (
        <div className="wizard-labels-section">
          <h3 className="wizard-subtitle">Photo Labels</h3>
          <div className="wizard-progress-wrap">
            <Progress value={labelCompleteness.percent} />
            <Badge tone={labelCompleteness.remaining > 0 ? "warning" : "success"}>
              {labelCompleteness.complete}/{labelCompleteness.total}
            </Badge>
          </div>
          <p className="wizard-muted">
            {labelCompleteness.remaining > 0
              ? `${labelCompleteness.remaining} photo(s) still need room and description.`
              : "All photo labels are complete."}
          </p>
          {state.photoSyncError ? (
            <p className="wizard-warning">{state.photoSyncError}</p>
          ) : null}
          <ul className="wizard-label-list">
            {summaryPhotoRows.map((photo, index) => {
              const draft = photoEditDrafts[photo.id] || {};
              const saveState = photoSaveState[photo.id] || { saving: false, reanalyzing: false, error: "" };
              const room = Object.prototype.hasOwnProperty.call(draft, "room") ? draft.room : photo.room || "";
              const description = Object.prototype.hasOwnProperty.call(draft, "description")
                ? draft.description
                : photo.description || "";
              return (
                <li
                  key={`${photo.id}-${index}`}
                  className={`wizard-label-row${dragPhotoId === photo.id ? " dragging" : ""}${dragOverPhotoId === photo.id ? " drag-over" : ""}`}
                  draggable
                  onDragStart={(e) => {
                    setDragPhotoId(photo.id);
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", photo.id);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setDragOverPhotoId(photo.id);
                  }}
                  onDragLeave={() => setDragOverPhotoId((current) => (current === photo.id ? "" : current))}
                  onDrop={(e) => {
                    e.preventDefault();
                    handlePhotoDrop(photo.id);
                  }}
                  onDragEnd={() => {
                    setDragPhotoId("");
                    setDragOverPhotoId("");
                  }}
                >
                  <div className="wizard-reorder-bar">
                    <div className="wizard-reorder-controls">
                      <div className="wizard-drag-handle" title="Drag to reorder" aria-label="Drag to reorder">
                        Drag
                      </div>
                    </div>
                    <div className="wizard-reorder-controls">
                      <button
                        type="button"
                        className="wizard-reorder-button"
                        onClick={() => handlePhotoMoveByOffset(photo.id, -1)}
                        disabled={index === 0}
                        aria-label="Move photo up"
                        title="Move up"
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        className="wizard-reorder-button"
                        onClick={() => handlePhotoMoveByOffset(photo.id, 1)}
                        disabled={index === summaryPhotoRows.length - 1}
                        aria-label="Move photo down"
                        title="Move down"
                      >
                        Down
                      </button>
                    </div>
                  </div>
                  <div className="wizard-label-main">
                    {photoBlobUrls[photo.id] || photo.thumbnail_url ? (
                      <button
                        type="button"
                        className="wizard-photo-thumb-btn"
                        onClick={() =>
                          setPreviewImage({
                            url: photoBlobUrls[photo.id] || photo.thumbnail_url,
                            name: photo.filename || `Photo ${index + 1}`,
                          })
                        }
                      >
                        <img
                          className="wizard-photo-thumb"
                          src={photoBlobUrls[photo.id] || photo.thumbnail_url}
                          alt={photo.filename || `Photo ${index + 1}`}
                        />
                      </button>
                    ) : (
                      <div className="wizard-photo-thumb wizard-photo-thumb-empty">No preview</div>
                    )}
                    <div className="wizard-label-fields">
                      <div className="wizard-photo-name">{photo.filename || `Photo ${index + 1}`}</div>
                      <p className="wizard-muted" style={{ margin: "6px 0 0" }}>
                        Confidence: {typeof photo.confidence === "number" && photo.confidence > 0 ? `${Math.round(photo.confidence * 100)}%` : "N/A"}
                      </p>
                      <div className="wizard-actions" style={{ marginTop: 6 }}>
                        {(() => {
                          const source = String(photo.analysis_source || "").toLowerCase();
                          const userLearned = source.includes("user") || source.includes("learn") || source.includes("manual");
                          return (
                            <span
                              className="wizard-source-mark"
                              title={userLearned ? "User learned label" : "Vision model label"}
                              aria-label={userLearned ? "User learned label" : "Vision model label"}
                            >
                              {userLearned ? "U" : "V"}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="wizard-photo-meta-grid" style={{ marginTop: 8 }}>
                        <div className="wizard-clearable-field">
                          <Input
                            className="wizard-input"
                            list="photo-room-memory"
                            value={room}
                            placeholder="Room"
                            onChange={(e) => handlePhotoDraftChange(photo.id, "room", e.target.value)}
                          />
                          {room ? (
                            <button
                              type="button"
                              className="wizard-field-clear"
                              aria-label="Clear room"
                              title="Clear room"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => handleClearPhotoField(e, photo.id, "room")}
                            >
                              x
                            </button>
                          ) : null}
                        </div>
                        <div className="wizard-clearable-field wizard-description-field">
                          <textarea
                            className="wizard-input wizard-description-textarea"
                            list="photo-description-memory"
                            value={description}
                            placeholder="Description"
                            onChange={(e) => handlePhotoDraftChange(photo.id, "description", e.target.value)}
                          />
                          {description ? (
                            <button
                              type="button"
                              className="wizard-field-clear"
                              aria-label="Clear description"
                              title="Clear description"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => handleClearPhotoField(e, photo.id, "description")}
                            >
                              x
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="wizard-actions">
                        <Button
                          variant="secondary"
                          onClick={() => handleSavePhoto(photo)}
                          disabled={saveState.saving || saveState.reanalyzing}
                        >
                          {saveState.saving ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleReanalyze(photo)}
                          disabled={saveState.saving || saveState.reanalyzing}
                        >
                          {saveState.reanalyzing ? "Re-analyzing..." : "Re-analyze"}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => handleDeleteReviewedPhoto(photo)}
                          disabled={saveState.saving || saveState.reanalyzing}
                        >
                          Delete
                        </Button>
                        {saveState.error ? <span className="wizard-warning">{saveState.error}</span> : null}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      handleCopyLabel(
                        `Room: ${formatRoomName(room || "")} | Description: ${description || ""}`,
                        index
                      )
                  }
                >
                  {copiedLabelIndex === index ? "Copied" : "Copy"}
                  </Button>
                </li>
              );
            })}
          </ul>
          <div className="wizard-muted" style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10, fontSize: 12 }}>
            <span><span className="wizard-source-mark" aria-hidden="true">V</span> Vision model</span>
            <span><span className="wizard-source-mark" aria-hidden="true">U</span> User learned</span>
          </div>
        </div>
      ) : null}
      {state.wantsPhotoLabels && summaryPhotoRows.length < 1 ? (
        <p className="wizard-warning">
          No backend photo labels available yet. Re-upload photos to sync with backend.
        </p>
      ) : null}

      <div className="wizard-actions">
        {state.wantsPhotoLabels && summaryPhotoRows.length > 1 ? (
          <Button variant="secondary" onClick={handleSaveAllPhotos} disabled={!hasUnsavedPhotoDrafts}>
            Save All Labels
          </Button>
        ) : null}
        {state.wantsLineItemSuggestions ? (
          <Button
            onClick={() => setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A7 }))}
            disabled={hasUnsavedPhotoDrafts || labelCompleteness.remaining > 0 || canUseLineItemSuggestions === false}
          >
            Create Line Item Suggestions
          </Button>
        ) : null}
        <Button
          variant="secondary"
          onClick={handleRetryPackCheck}
          disabled={!state.jobId || packCheckRetrying}
        >
          {packCheckRetrying ? "Checking Pack..." : "Retry Pack Check"}
        </Button>
        <Button
          onClick={handleDownloadPack}
          disabled={!state.packReady || state.downloadingPack || !state.jobId}
        >
          {state.downloadingPack ? "Preparing Download..." : "Download Estimator Pack (.zip)"}
        </Button>
        <Button
          variant="secondary"
          onClick={handleDownloadAnnotatedPhotos}
          disabled={!state.jobId || state.downloadingAnnotatedPhotos || summaryPhotoRows.length < 1}
        >
          {state.downloadingAnnotatedPhotos ? "Preparing Photos..." : "Download Annotated Photos (.zip)"}
        </Button>
        <Button variant="secondary" onClick={startAnotherBuild}>
          Start another build
        </Button>
      </div>
      {state.processingError ? <p className="wizard-warning">{state.processingError}</p> : null}
      </CardBody>
    </Card>
  );

  const renderA7 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Line Item Review</h1>
        <p className="wizard-subtitle">
          Review suggested line items grouped by approved room labels and affected surface.
        </p>
      </CardHeader>
      <CardBody>
      {canUseLineItemSuggestions === false ? (
        <>
          <p className="wizard-warning">Line item suggestions are available on paid plans. Photo labels remain available on the free plan.</p>
          <div className="wizard-actions">
            <Button variant="secondary" onClick={() => setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }))}>
              Back to Label Review
            </Button>
          </div>
        </>
      ) : (
      <>
      <AiDisclaimer
        title="Line Item Suggestion Disclaimer"
        detail="Suggested line items and quantities are AI-assisted and may miss scope or include inaccuracies. Validate against site conditions and carrier requirements."
      />
      {state.lineItemsLoading ? (
        <p className="wizard-muted inline-flex items-center gap-2"><Spinner size="sm" />Loading suggested line items...</p>
      ) : state.lineItems.length < 1 && state.lineItemsConfirmedEmpty ? (
        <p className="wizard-warning">No suggested line items found yet. Run processing again or retry.</p>
      ) : (
        <div className="space-y-4">
          {groupedLineItemsByRoom.map((roomGroup) => (
            <div key={roomGroup.room} className="space-y-3">
              <h3 className="text-xl font-semibold tracking-tight text-zinc-900" style={{ marginTop: 0 }}>
                Room: {formatRoomName(roomGroup.room)}
              </h3>
              {roomGroup.areas.map((areaGroup) => (
                <div key={`${roomGroup.room}-${areaGroup.area}`} style={{ overflowX: "auto" }}>
                  <h4 className="wizard-muted" style={{ margin: "0 0 8px" }}>
                    {areaGroup.area}
                  </h4>
                  <table className="wizard-lineitems-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Copy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {areaGroup.items.map((item, index) => (
                        <tr key={`${roomGroup.room}-${areaGroup.area}-${item.key}`}>
                          <td>{item.description || ""}</td>
                          <td>{item.qty === "" ? "" : String(item.qty)}</td>
                          <td>{item.unit || ""}</td>
                          <td>
                            <Button size="sm" variant="secondary" onClick={() => handleCopyLineItemDescription(item, index)}>
                              {state.copiedCodeIndex === index ? "Copied" : "Copy"}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={handleRegenerateLineItems} className="ml-2">
                              Regenerate
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="wizard-actions">
        <Button variant="secondary" onClick={() => setState((prev) => ({ ...prev, currentStepId: STEP_IDS.A6 }))}>
          Back to Label Review
        </Button>
        <Button variant="ghost" onClick={handleRegenerateLineItems}>
          Regenerate
        </Button>
        <Button
          variant="secondary"
          onClick={handleRetryPackCheck}
          disabled={!state.jobId || packCheckRetrying || canUseLineItemSuggestions === false}
        >
          {packCheckRetrying ? "Checking Pack..." : "Retry Pack Check"}
        </Button>
        <Button
          onClick={handleDownloadPack}
          disabled={!state.packReady || state.downloadingPack || !state.jobId || canUseLineItemSuggestions === false}
        >
          {state.downloadingPack ? "Preparing Download..." : "Download Estimator Pack (.zip)"}
        </Button>
        <Button
          variant="secondary"
          onClick={handleDownloadAnnotatedPhotos}
          disabled={!state.jobId || state.downloadingAnnotatedPhotos || summaryPhotoRows.length < 1}
        >
          {state.downloadingAnnotatedPhotos ? "Preparing Photos..." : "Download Annotated Photos (.zip)"}
        </Button>
      </div>
      </>
      )}
      </CardBody>
    </Card>
  );

  let stepContent = null;
  if (state.currentStepId === STEP_IDS.A0) stepContent = renderA0();
  else if (state.currentStepId === STEP_IDS.A1) stepContent = renderA1();
  else if (state.currentStepId === STEP_IDS.A2) stepContent = renderA2();
  else if (state.currentStepId === STEP_IDS.A3) stepContent = renderA3();
  else if (state.currentStepId === STEP_IDS.A4) stepContent = renderA4();
  else if (state.currentStepId === STEP_IDS.A5) stepContent = renderA5();
  else if (state.currentStepId === STEP_IDS.A6) stepContent = renderA6();
  else stepContent = renderA7();

  return (
    <div className="build-wizard-page">
      {flash.text ? <div className={`wizard-flash ${flash.type === "error" ? "error" : "success"}`}>{flash.text}</div> : null}
      <div className="wizard-top-actions">
        <Button variant="secondary" onClick={handleCancelBuild}>
          Cancel Build
        </Button>
      </div>
      <WizardStepper steps={WIZARD_STEPS} currentStepId={state.currentStepId} onStepClick={handleStepJump} />
      {stepContent}
      {previewImage ? (
        <div className="wizard-image-modal" role="dialog" aria-modal="true" onClick={() => setPreviewImage(null)}>
          <div className="wizard-image-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="wizard-image-modal-head">
              <span className="wizard-photo-name">{previewImage.name}</span>
              <Button size="sm" variant="secondary" onClick={() => setPreviewImage(null)}>
                Close
              </Button>
            </div>
            <img className="wizard-image-modal-img" src={previewImage.url} alt={previewImage.name} />
          </div>
        </div>
      ) : null}
      <datalist id="photo-room-memory">
        {roomLabelMemory.map((value) => <option key={`room-memory-${value}`} value={value} />)}
      </datalist>
      <datalist id="photo-description-memory">
        {descriptionLabelMemory.map((value) => <option key={`desc-memory-${value}`} value={value} />)}
      </datalist>
    </div>
  );
}
